import { Canvas, Line, FabricImage, FabricObject, FabricText, Polygon, PencilBrush } from "fabric";
import { defer, Subject } from "rxjs";
import { mergeMap, takeUntil } from "rxjs/operators";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { InspectionImage, MeasurementData, MeasurementInfo } from "../../../_objects/inspection-image";
import {
  AnnotationEmitData,
  AreaLines3D,
  AreaLinesIV7,
  AreaLinesMeasureData_WithReturnCode,
  AreaLinesNX,
  AreaMeasureData_NX_WithReturnCode,
  Line2D,
  LinesMeasureData_IV7_WithReturnCode,
  LinesMeasureData_NX_WithReturnCode,
  PlotResult,
  PlotResultGroup,
  PointSet,
  PointSet_IV7,
  PointSet_IV7_WithReturnCode,
  PointSet_NX,
  PointSet_NX_WithReturnCode,
  PointSetCommonResultWithCode,
  PointToLineMeasureData,
  PointToLineMeasureData_IV7_WithReturnCode,
  PointToLineMeasureData_NX,
  PointToLineMeasureData_NX_WithReturnCode,
  PointToLineMeasureData_WithReturnCode,
  PolylineData,
  PolylineData_IV7_WithReturnCode,
  StereoReconstructionResult,
  Vertices2Df,
  Vertices3Df
} from "../../../_objects/remeasurement";
import { CommonSnackBarService } from "../../../_services/common-snack-bar.service";
import { InspectionImageService } from "../../../_services/http-api/inspection-image.service";
import { TenantConfigService } from "../../../_services/http-api/tenant-config.service";
import { MeasurementService } from "../../../_services/measurement.service";
import { NetworkStatusService } from "../../../_services/network-status.service";
import { SpinnerService } from "../../../_services/spinner.service";
import { MEASUREMENT_IMAGE_MODE, MEASUREMENT_MODE, MODE, TenantConfig, USE_IMAGE_TYPE } from "../../../app.constants";
import { CommonConfirmDialogComponent } from "../../../common-dialog/common-confirm-dialog.component";
import { SelectMeasurementModeDialogComponent } from "./select-measurement-mode-dialog/select-measurement-mode-dialog.component";
import { BlobService } from "../../../_services/blob.service";
import { IconEnum } from "@evc/web-components";
import { EvcButtonSize, EvcButtonType, EvcButtonVariant } from "../../../shared/enumerations/ui-enumerations";
import { MeasurementResult, MeasurementResultFactory } from "../../../_services/inspection/measurement-result.factory";

@Component({
  selector: "app-image-measurement-edit",
  templateUrl: "./image-measurement-edit.component.html",
  styleUrls: ["./image-measurement-edit.component.scss"],
  standalone: false
})
export class ImageMeasurementEditComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  readonly iconEnums = IconEnum;
  readonly buttonTypes = EvcButtonType;
  readonly buttonSize = EvcButtonSize;
  readonly buttonVariants = EvcButtonVariant;

  @Input() inspectionImage: InspectionImage;
  @Input() annotationObjects: any = null;
  @Input() isEditable: boolean;
  @Input() isDataLoaded = false;

  @Output() modified = new EventEmitter<AnnotationEmitData>();

  @ViewChild("3dCanvas")
  private canvasRef: ElementRef;

  readonly distance = [MEASUREMENT_MODE.DISTANCE, "Distance", "二点間", "距离"];
  readonly pointToLine = [MEASUREMENT_MODE.POINT_TO_LINE, "Point to Line", "線基準", "点对线"];
  readonly depth = [MEASUREMENT_MODE.DEPTH, "Depth", "面基準", "深度"];
  readonly lines = [MEASUREMENT_MODE.LINES, "Lines", "全長", "线"];
  readonly area = [MEASUREMENT_MODE.AREA, "Area", "面積", "面积"];
  readonly DUMMYCANVAS = "dummyCanvas";
  readonly LEFTCANVAS = "leftCanvas";
  readonly RIGHTCANVAS = "rightCanvas";
  readonly units = ["mm", "inch"];

  /** Set an interval so that the communication process does not overtake the rendering process while loading EXIF data */
  readonly LoadMeasurementInterval = 0.1;

  readonly iplexAppName = "IPLEX app";
  readonly visolAppName = "ViSOL app";
  public measurementInfoData: StereoReconstructionResult;
  public dummyCanvas: Canvas;
  public rightCanvas: Canvas;
  public leftCanvas: Canvas;
  public imageSrc: string;
  public imgSrcBase64: string;
  public uvImgSrcBase64: string;
  public initDataJson: string;
  public selectMode: string;
  public selectResult: MeasurementResult;
  public unitSelected: string;
  public maxPoints = 0;
  public measurementResult: MeasurementResult[] = [];
  public measurementNumData: any[] = [];
  public plotPointList = [];
  public measurementColor = "#ffff00";
  public referenceLineColor = "#00ffff";
  public areaFillColor = "#401678";
  public isCanvasLoading = true;
  public initLoaded = false;
  public isReadyMeasurement = true;
  public isLoadImage = false;
  public isImageInfo = false;
  public isLoadMeasurementData = false;
  public isLMeasurement = false; // Measuring on the left
  public isRMeasurement = false; // Measuring on the right
  public isLMeasurement2 = false; // Measuring on the left
  public isRMeasurement2 = false; // Measuring on the right
  public isApiError = false; // Flag for when the response is obtained more than 5 times / ReturnCode is invalid
  public is3DMode = true; // Whether it is displayed in 3D mode or not
  public isReady3d = false;
  public imageMagnification = 1;
  public scale;
  public positionZ = 0;
  public referenceLine = [];
  public referenceSurface = [];
  public retentionResult: PlotResultGroup[] = [];
  public uvImage: string;
  public loadMeasurementPressed: boolean = false;
  public measurementModes = [];

  private firstActiveObjIndex = -1;
  private onDestroy$ = new Subject();

  private camera: THREE.PerspectiveCamera;

  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;

  public cameraZ = 25;

  public fieldOfView = 70;

  public nearClippingPane = 0.1;

  public farClippingPane = 1000;

  public tmpDummyCanvasObj = [];
  public tmpLeftCanvasObj = [];
  public tmpRightCanvasObj = [];
  public tmpPlotPointList = [];
  public tmpResult = [];
  public tmpReferenceSurface = [];
  public tmpReferenceLine = [];
  public tmpRetentionResult = [];
  public halfMainImgW: number; // Half the width of the display canvas (in display scale size)
  public resizeTimer = null;
  public mainImg: HTMLImageElement;
  public isLoading = false; // During measurement
  public isWaitLoading = false; // Whether waiting for the end of measurement
  public isSelectMode = false; // Whether in a mode for selecting marked objects
  public movePointList = [];
  public movePointResult = []; // Point data with left and right data
  public selectData = []; // Holds data of selected annotations
  public tmpDCanvas; // Temporarily store DummyCanvas data before moving points
  public tmpLCanvas; // Temporarily store LeftCanvas data before moving points
  public tmpRCanvas; // Temporarily store RightCanvas data before moving points
  public tmpTCanvas; // Temporarily store 3DCanvas data before moving points
  public tmpMeasureResult; // Temporarily store measurement results before moving points
  public measureCount; // Tracks which measurement is currently loaded in auto-load
  public isDuringMeasure = false; // Whether measurement is mid-process
  public duringMeasureId: string; // Holds ID of the measurement in progress
  private isPointAddingToCanvas: boolean = false;
  private isThreeDApplicable: boolean = false;
  private isCurveError = false;
  public firstCameraPosition; // Used to hold the initial camera position

  private get canvas(): HTMLCanvasElement {
    return this.canvasRef.nativeElement;
  }

  public get isOnPremiseMode(): boolean {
    return this.networkStatusService.isOnPremise;
  }

  private async sleep(sec: number) {
    return await new Promise((resolve) => setTimeout(resolve, sec * 1000));
  }

  constructor(
    private oscSpinnerService: SpinnerService,
    private inspectionImageService: InspectionImageService,
    private snackBar: CommonSnackBarService,
    private dialog: MatDialog,
    private measurementService: MeasurementService,
    private translate: TranslateService,
    private tenantConfigService: TenantConfigService,
    private networkStatusService: NetworkStatusService,
    private cd: ChangeDetectorRef,
    private blobService: BlobService
  ) {}

  ngOnInit() {
    this.setConfig();
    this.onInspectionImageChange();
  }

  ngAfterViewInit() {
    this.displayModeSetting();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes.isDataLoaded && this.isDataLoaded) {
      this.initInspectionImage();
    }
  }

  private onInspectionImageChange() {
    this.measurementService.getInspectionImage.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.setMeasurementModes();
    });
  }

  private setMeasurementModes() {
    const defaultModes = [
      MEASUREMENT_MODE.DISTANCE,
      MEASUREMENT_MODE.POINT_TO_LINE,
      MEASUREMENT_MODE.DEPTH,
      MEASUREMENT_MODE.LINES,
      MEASUREMENT_MODE.AREA
    ];
    const measurementType = this.measurementService.getMeasurementType;
    switch (measurementType) {
      case MEASUREMENT_IMAGE_MODE.NX:
      case MEASUREMENT_IMAGE_MODE.IV7:
        this.measurementModes = defaultModes;
        break;
      case MEASUREMENT_IMAGE_MODE.ThreeD:
        this.measurementModes = [...defaultModes, MEASUREMENT_MODE.MISSING_CORNER];
        break;
    }
  }

  private async initInspectionImage() {
    if (this.inspectionImage && !this.initLoaded) {
      this.initLoaded = true;
      this.measurementService.setInspectionImage(this.inspectionImage);
      await this.getAndGenerationOfMeasureInfo(false);
      this.isThreeDApplicable =
        this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.NX ||
        this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.ThreeD;
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
    clearTimeout(this.resizeTimer);
  }

  // TenantConfig取得
  setConfig() {
    const configs = this.tenantConfigService.tenantConfig;
    if (configs) {
      const val = configs.find((_) => _.key === TenantConfig.DefaultUnitOfMeasurementResult.KEY);
      this.unitSelected = val.selectItems[val.value as string].label as string;
    }
  }

  private async loadMeasurementData(measurementInfo: MeasurementInfo): Promise<void> {
    this.isLoadMeasurementData = true;
    if (measurementInfo.measurementCount === 0) {
      this.isLoadMeasurementData = false;
      this.initCanvasAndResult();
      this.oscSpinnerService.hide(true);
      this.resizeAfterLoading();
      return;
    }

    this.initCanvasAndResult();
    for (let i = 0; i < measurementInfo.measurementCount; i++) {
      this.measureCount = i;
      if (i > 0) {
        // Proceed to the next measurement before the rendering process is complete.
        await this.sleep(this.LoadMeasurementInterval);
      }

      const measurementDataList = measurementInfo.measurementDataList[i];
      this.plotPointList = [];

      this.measurementService.setMeasurementModeFromEXIF(measurementDataList.measurementType);
      this.selectMode = this.measurementService.getMeasurementMode;

      this.changeSelectMode(this.selectMode);

      if (this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.NX) {
        await this.measureFromEXIFNX(measurementInfo.measurementDataList[i]);
      } else if (this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.IV7) {
        await this.measureFromEXIFIV7(measurementInfo.measurementDataList[i], i);
      } else {
        await this.measureFromEXIF3D(measurementInfo.measurementDataList[i]);
      }
    }

    if (this.measurementResult.length > 0) {
      this.selectResult = this.measurementResult[this.measurementResult.length - 1];
    }
    this.isLoadMeasurementData = false;
    this.oscSpinnerService.hide(true);
    this.resizeAfterLoading();
  }

  /**
   * Reflect the measurement results on the screen based on the information obtained from EXIF for 2D image data
   * @param data Measurement data
   * @param measurementCount If 0, a reference line needs to be drawn
   */
  private async measureFromEXIFIV7(data: MeasurementData, measurementCount: number) {
    const pointCnt = data.points.filter((_) => _.x !== 0 && _.y !== 0).length;

    let pointList = [];
    if (this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE || this.selectMode === MEASUREMENT_MODE.DEPTH) {
      // Reference line
      if (measurementCount === 0) {
        for (let j = 0; j < data.references.length; j++) {
          if (data.references[j].x !== 0 && data.references[j].y !== 0) {
            await this.measureDotCoordinateDataSetAndDrawingAsync(data.references[j], true);

            // Save reference line
            if (this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE && !this.isLineReferenceMeasure() && this.plotPointList.length === 2) {
              this.referenceLine = this.plotPointList;
            } else if (this.selectMode === MEASUREMENT_MODE.DEPTH && !this.isSurfaceReferenceMeasure() && this.plotPointList.length === 3) {
              this.referenceSurface = this.plotPointList;
            }

            // IV7 line-based measurement
            if (this.plotPointList.length >= 2) {
              await this.distortionReproductionCurveAcquisitionFromEXIF(false, null);
            }
          }
        }
      }

      // Measurement points
      if (data.points[0] && data.points[0].x !== 0 && data.points[0].y !== 0) {
        await this.measureDotCoordinateDataSetAndDrawingAsync(data.points[0], true);

        if (this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE && this.isLineReferenceMeasure()) {
          // For subsequent measurements, use the retained reference line
          pointList.push(this.referenceLine[0]);
          pointList.push(this.referenceLine[1]);
          pointList.push(this.plotPointList[0]);
        } else if (this.selectMode === MEASUREMENT_MODE.DEPTH && this.isSurfaceReferenceMeasure()) {
          // For subsequent measurements, use the retained reference surface
          pointList.push(this.referenceSurface[0]);
          pointList.push(this.referenceSurface[1]);
          pointList.push(this.referenceSurface[2]);
          pointList.push(this.plotPointList[0]);
        } else {
          pointList = this.plotPointList;
        }

        // Since line-based measurement results are not included in EXIF, obtain them individually
        if (
          this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE &&
          (this.plotPointList.length === 3 || (this.isLineReferenceMeasure() && this.plotPointList.length === 1))
        ) {
          await this.measurementService
            .getLineReferenceMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, pointList)
            .then(async (item: PointToLineMeasureData_IV7_WithReturnCode) => {
              const res = item.pointToLineResult;
              const setPoint = new Vertices2Df(res.foot[0].x, res.foot[0].y);
              const resPoint = new Vertices2Df(res.foot[1].x, res.foot[1].y);
              const threeDPoint = new Vertices3Df(res.pntset_meas.pnt_3d.x, res.pntset_meas.pnt_3d.y, res.pntset_meas.pnt_3d.z);
              this.dataset2d(setPoint, resPoint, true, threeDPoint);
              // Group the plotted coordinates
              const result = new PointSet_IV7();
              result.msr_pnt = item.pointToLineResult.foot[0];
              result.res_pnt = item.pointToLineResult.foot[1];
              result.pnt_3d = res.pntset_meas.pnt_3d;
              result.fmatch = res.pntset_meas.fmatch;
              this.plotPointList = pointList;
              this.plotPointList.push(result);
              await this.sleep(this.LoadMeasurementInterval);
            })
            .catch((err) => this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError")));
        }
      }
      await this.setResult(
        this.selectMode,
        true,
        this.selectMode === MEASUREMENT_MODE.DEPTH ? data.depth : data.length,
        data.objectDistance,
        data.area,
        data.subResult
      );
    } else {
      for (let i = 0; i < pointCnt; i++) {
        await this.drawingMeasurementResultAsync(i, pointCnt, data);
      }
    }
  }

  /**
   * Reflect the measurement results on the screen based on the information obtained from EXIF for 3D image data
   * @param data Measurement data
   */
  private async measureFromEXIFNX(data: MeasurementData): Promise<void> {
    let pointList = [];

    const pointCnt = data.points.filter((_) => _.x !== 0 && _.y !== 0).length;
    if (this.selectMode == MEASUREMENT_MODE.POINT_TO_LINE || this.selectMode == MEASUREMENT_MODE.DEPTH) {
      for (let j = 0; j < pointCnt; j++) {
        if (
          (this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE && this.isLineReferenceMeasure() && j < 2) ||
          (this.selectMode === MEASUREMENT_MODE.DEPTH && this.isSurfaceReferenceMeasure() && j < 3)
        ) {
          continue;
        }

        await this.measureDotCoordinateDataSetAndDrawingAsync(data.points[j], true);

        // Save reference line
        if (this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE && !this.isLineReferenceMeasure() && this.plotPointList.length === 2) {
          this.referenceLine = this.plotPointList;
        } else if (this.selectMode === MEASUREMENT_MODE.DEPTH && !this.isSurfaceReferenceMeasure() && this.plotPointList.length === 3) {
          this.referenceSurface = this.plotPointList;
        }

        if (this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE && this.isLineReferenceMeasure()) {
          // For subsequent measurements, use the retained reference line
          pointList.push(this.referenceLine[0]);
          pointList.push(this.referenceLine[1]);
          pointList.push(this.plotPointList[0]);
        } else if (this.selectMode === MEASUREMENT_MODE.DEPTH && this.isSurfaceReferenceMeasure()) {
          // For subsequent measurements, use the retained reference surface
          pointList.push(this.referenceSurface[0]);
          pointList.push(this.referenceSurface[1]);
          pointList.push(this.referenceSurface[2]);
          pointList.push(this.plotPointList[0]);
        } else {
          pointList = this.plotPointList;
        }

        // Since line-based measurement results are not included in EXIF, obtain them individually
        if (
          this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE &&
          (this.plotPointList.length === 3 || (this.isLineReferenceMeasure() && this.plotPointList.length === 1))
        ) {
          await this.measurementService
            .getLineReferenceMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, pointList)
            .then(async (item: PointToLineMeasureData_NX_WithReturnCode) => {
              const res = item.pointToLineResult;
              const setPoint = new Vertices2Df(res.foot.msr_pnt.x, res.foot.msr_pnt.y);
              const resPoint = new Vertices2Df(res.foot.res_pnt.x, res.foot.res_pnt.y);
              const threeDPoint = new Vertices3Df(res.foot.pnt_3d.x, res.foot.pnt_3d.y, res.foot.pnt_3d.z);
              this.dataset2d(setPoint, resPoint, true, threeDPoint);
              // Group the plotted coordinates
              const result = new PointSet();
              result.msr_pnt = res.foot.msr_pnt;
              result.res_pnt = res.foot.res_pnt;
              result.msr_pnt_cor = res.foot.msr_pnt_cor;
              result.res_pnt_cor = res.foot.res_pnt_cor;
              result.pnt_3d = res.foot.pnt_3d;
              this.plotPointList = pointList;
              this.plotPointList.push(result);
              await this.sleep(this.LoadMeasurementInterval);
            })
            .catch((err) => this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError")));
        }
        await this.setResult(this.selectMode, j === pointCnt - 1, data.length, data.objectDistance, data.area, data.subResult);
      }
    } else {
      for (let i = 0; i < pointCnt; i++) {
        await this.drawingMeasurementResultAsync(i, pointCnt, data);
      }
    }
  }

  /**
   * Reflect the measurement results on the screen based on the information obtained from EXIF for 3D image data
   * @param data Measurement data
   */
  private async measureFromEXIF3D(data: MeasurementData): Promise<void> {
    let pointList = [];

    const pointCnt = data.points.filter((_) => _.x !== 0 && _.y !== 0).length;
    if (this.selectMode == MEASUREMENT_MODE.POINT_TO_LINE || this.selectMode == MEASUREMENT_MODE.DEPTH) {
      for (let j = 0; j < pointCnt; j++) {
        if (
          (this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE && this.isLineReferenceMeasure() && j < 2) ||
          (this.selectMode === MEASUREMENT_MODE.DEPTH && this.isSurfaceReferenceMeasure() && j < 3)
        ) {
          continue;
        }

        await this.measureDotCoordinateDataSetAndDrawingAsync(data.points[j], true);

        // Save reference line
        if (this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE && !this.isLineReferenceMeasure() && this.plotPointList.length === 2) {
          this.referenceLine = this.plotPointList;
        } else if (this.selectMode === MEASUREMENT_MODE.DEPTH && !this.isSurfaceReferenceMeasure() && this.plotPointList.length === 3) {
          this.referenceSurface = this.plotPointList;
        }

        if (this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE && this.isLineReferenceMeasure()) {
          // For subsequent measurements, use the retained reference line
          pointList.push(this.referenceLine[0]);
          pointList.push(this.referenceLine[1]);
          pointList.push(this.plotPointList[0]);
        } else if (this.selectMode === MEASUREMENT_MODE.DEPTH && this.isSurfaceReferenceMeasure()) {
          // For subsequent measurements, use the retained reference surface
          pointList.push(this.referenceSurface[0]);
          pointList.push(this.referenceSurface[1]);
          pointList.push(this.referenceSurface[2]);
          pointList.push(this.plotPointList[0]);
        } else {
          pointList = this.plotPointList;
        }

        // Since line-based measurement results are not included in EXIF, obtain them individually
        if (
          this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE &&
          (this.plotPointList.length === 3 || (this.isLineReferenceMeasure() && this.plotPointList.length === 1))
        ) {
          await this.measurementService
            .getLineReferenceMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, pointList)
            .then(async (item: PointToLineMeasureData_WithReturnCode) => {
              const res = item.pointToLineResult;
              const setPoint = new Vertices2Df(res.foot.msr_pnt.x, res.foot.msr_pnt.y);
              const resPoint = new Vertices2Df(res.foot.res_pnt.x, res.foot.res_pnt.y);
              const threeDPoint = new Vertices3Df(res.foot.pnt_3d.x, res.foot.pnt_3d.y, res.foot.pnt_3d.z);
              this.dataset2d(setPoint, resPoint, true, threeDPoint);
              // Group the plotted coordinates
              const result = new PointSet();
              result.msr_pnt = res.foot.msr_pnt;
              result.res_pnt = res.foot.res_pnt;
              result.msr_pnt_cor = res.foot.msr_pnt_cor;
              result.res_pnt_cor = res.foot.res_pnt_cor;
              result.pnt_3d = res.foot.pnt_3d;
              this.plotPointList = pointList;
              this.plotPointList.push(result);
              await this.sleep(this.LoadMeasurementInterval);
            })
            .catch((err) => this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError")));
        }
        await this.setResult(this.selectMode, j === pointCnt - 1, data.length, data.objectDistance, data.area, data.subResult);
      }
    } else {
      for (let i = 0; i < pointCnt; i++) {
        await this.drawingMeasurementResultAsync(i, pointCnt, data);
      }
    }
  }

  /**
   * Asynchronously obtain point positions and line segment data for rendering (used when loading measurement data)
   */
  private async drawingMeasurementResultAsync(pointNum: number, pointCnt: number, data: MeasurementData): Promise<number> {
    let lineCoordinateResult: LineCoordinateResult;
    if (pointNum === pointCnt - 1) {
      await this.measureDotCoordinateDataSetAndDrawingAsync(data.points[pointNum], true);
      if (pointCnt === 1) {
        return;
      }

      if (this.selectMode !== MEASUREMENT_MODE.DISTANCE) {
        let startPoint1: Vertices3Df = null;
        let endPoint1: Vertices3Df = null;
        const length: number = pointCnt === 2 || pointCnt === 3 ? 0 : pointCnt - 3;
        const lPlot1: Vertices2Df = new Vertices2Df(this.plotPointList[length].msr_pnt.x, this.plotPointList[length].msr_pnt.y);
        const lPlot2: Vertices2Df = new Vertices2Df(this.plotPointList[length + 1].msr_pnt.x, this.plotPointList[length + 1].msr_pnt.y);
        const rPlot1: Vertices2Df = new Vertices2Df(this.plotPointList[length].res_pnt.x, this.plotPointList[length].res_pnt.y);
        const rPlot2: Vertices2Df = new Vertices2Df(this.plotPointList[length + 1].res_pnt.x, this.plotPointList[length + 1].res_pnt.y);

        if (this.isThreeDApplicable) {
          startPoint1 = new Vertices3Df(
            this.plotPointList[length].pnt_3d.x,
            this.plotPointList[length].pnt_3d.y,
            this.plotPointList[length].pnt_3d.z
          );
          endPoint1 = new Vertices3Df(
            this.plotPointList[length + 1].pnt_3d.x,
            this.plotPointList[length + 1].pnt_3d.y,
            this.plotPointList[length + 1].pnt_3d.z
          );
        }
        lineCoordinateResult = await this.getLineCoordinatesAsync([lPlot1, lPlot2], [rPlot1, rPlot2], [startPoint1, endPoint1], 0, false);
      }

      await this.setResult(this.selectMode, pointNum === pointCnt - 1, data.length, data.objectDistance, data.area, data.subResult);

      if (lineCoordinateResult?.requirePushMeasurementResult) {
        this.pushMeasurementResult();
      }
    } else {
      await this.measureDotCoordinateDataSetAndDrawingAsync(data.points[pointNum], true);
      await this.setResult(this.selectMode, pointNum === pointCnt - 1, data.length, data.objectDistance, data.area, data.subResult);
    }
    return pointNum;
  }

  /**
   * Measure the coordinate dataset of the points marked on the image
   * Render the obtained coordinate data onto LeftCanvas and RightCanvas
   * @param data
   * @param isLoad True if performing measurement by loading EXIF data
   */
  async measureDotCoordinateDataSetAndDrawingAsync(
    data: Vertices2Df,
    isLoad: boolean = false
  ): Promise<PointSetCommonResultWithCode | PointSet_IV7_WithReturnCode | PointSet_NX_WithReturnCode> {
    let pointSet: PointSetCommonResultWithCode | PointSet_IV7_WithReturnCode | PointSet_NX_WithReturnCode;
    if (this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.NX) {
      pointSet = new PointSet_NX_WithReturnCode();
      pointSet.pointSetResult.isMatch = false;
    } else if (this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.IV7) {
      pointSet = new PointSet_IV7_WithReturnCode();
      pointSet.pointSetResult.isMatch = false;
    } else {
      pointSet = new PointSetCommonResultWithCode();
      pointSet.pointSetResult.isMatch = false;
    }

    try {
      pointSet = await this.measurementService.getMeasureDotCoordinateDataSet(
        this.inspectionImage.inspectionId,
        this.inspectionImage.id,
        data,
        this.measurementInfoData.model3DData,
        this.dummyCanvas.width
      );
    } catch (err) {
      this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError"));
    }

    // If the request result's isMatch is False when manually marking, do not reflect the result
    // When loading measurement results, continue drawing to display the approximate value
    if ((pointSet.returnCode !== 0 || !pointSet.pointSetResult.isMatch) && !isLoad) {
      // When matching is unsuccessful for markings on the left and right Canvas
      this.snackBar.open(this.translate.instant("imageRemeasurementEdit.noMatch"));
      return pointSet;
    } else if ((pointSet.returnCode !== 0 || !pointSet.pointSetResult.isMatch) && this.selectData.length > 0) {
      this.isApiError = true;
    }
    const lastPoint = this.plotPointList[this.plotPointList.length - 1];
    const pointSetResult = pointSet.pointSetResult;
    const firstPoint = this.plotPointList[0];
    if (
      (this.plotPointList.length >= 3 &&
        this.isCrossingLines(lastPoint.msr_pnt.x, lastPoint.msr_pnt.y, pointSetResult.msr_pnt.x, pointSetResult.msr_pnt.y)) ||
      (this.plotPointList.length === 19 && this.selectMode === MEASUREMENT_MODE.AREA)
    ) {
      // Close the area when marking the 20th point or when marking intersects with the first and second points
      if (this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.NX) {
        const result = new PointSet_NX();
        result.msr_pnt = firstPoint.msr_pnt;
        result.res_pnt = firstPoint.res_pnt;
        result.msr_pnt_cor = firstPoint.msr_pnt_cor;
        result.res_pnt_cor = firstPoint.res_pnt_cor;
        result.pnt_3d = firstPoint.pnt_3d;
        result.acc_val = firstPoint.acc_val;
        result.isMatch = true;
        this.plotPointList.push(result);
        this.movePointResult.push(result);
      } else if (this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.IV7) {
        const result = new PointSet_IV7();
        result.msr_pnt = firstPoint.msr_pnt;
        result.res_pnt = firstPoint.res_pnt;
        result.pnt_3d = firstPoint.pnt_3d;
        result.fmatch = firstPoint.fmatch;
        result.isMatch = true;
        this.plotPointList.push(result);
        this.movePointResult.push(result);
      } else {
        const result = new PointSet();
        result.msr_pnt = firstPoint.msr_pnt;
        result.res_pnt = firstPoint.res_pnt;
        result.msr_pnt_cor = firstPoint.msr_pnt_cor;
        result.res_pnt_cor = firstPoint.res_pnt_cor;
        result.pnt_3d = firstPoint.pnt_3d;
        result.isMatch = true;
        this.plotPointList.push(result);
        this.movePointResult.push(result);
      }
    } else {
      this.dataset2d(pointSetResult.msr_pnt, pointSetResult.res_pnt, false, pointSetResult.pnt_3d);
      this.measurementNumSet(pointSetResult, null, null);

      this.plotPointList.push(pointSetResult);
      this.movePointResult.push(pointSetResult);
    }
    return pointSet;
  }

  /**
   * If isMeasure is False, only obtain and render the lines.
   * Reflect the measurement data from EXIF onto the screen
   * Obtain data that is not included in EXIF within the method
   * List of missing data: [ViSOL Measurement in EXIF](https://ossa-cloud.visualstudio.com/OSD%20Visol%20Solution/_wiki/wikis/OSD-Visol-Solution.wiki/652/ViSOL-Measurement-in-EXIF)
   */
  async setResult(mode: string, isMeasure: boolean, length: number, objectDistance: number, area: number, subResult: number = null): Promise<void> {
    if (this.isApiError) {
      return;
    }
    if (this.plotPointList.length >= 2) {
      await this.distortionReproductionCurveAcquisitionFromEXIF(isMeasure, null);
    }

    if (this.isDuringMeasure || (this.selectData.length > 0 && mode === MEASUREMENT_MODE.LINES)) {
      return;
    }

    if (isMeasure) {
      if (!objectDistance) {
        objectDistance = this.plotPointList[this.plotPointList.length - 1].pnt_3d.z;
      }

      if (subResult && this.selectMode === MEASUREMENT_MODE.AREA) {
        length = subResult;
      }

      const id = this.getResultId();
      if (this.selectMode === MEASUREMENT_MODE.LINES) {
        await this.measurementService
          .getTwoPointsMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, [
            this.plotPointList[this.plotPointList.length - 2],
            this.plotPointList[this.plotPointList.length - 1]
          ])
          .then((item) => {
            this.retentionResult.push(new PlotResultGroup(id, mode, length, objectDistance, area, item.distanceResult.distance, this.plotPointList));
          })
          .catch((err) => this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError")));
      } else {
        this.retentionResult.push(new PlotResultGroup(id, mode, length, objectDistance, area, null, this.plotPointList));
      }
      this.updateObjectInfoOnCanvas(
        this.retentionResult[0].length,
        this.retentionResult[0].objectDistance,
        this.retentionResult[0].id,
        this.retentionResult[0].area,
        this.retentionResult[0].latestDistance
      );
      this.measurementResult.push(MeasurementResultFactory.createMeasurementResult(this.retentionResult[0]));
      this.plotPointList = [];
      this.retentionResult = [];
    }
  }

  /**
   * Obtain and construct the configuration points of the distortion reproduction curve based on the information loaded from EXIF
   */
  private async distortionReproductionCurveAcquisitionFromEXIF(isMeasure: boolean, foot: any): Promise<void> {
    let lPlot1: Vertices2Df;
    let lPlot2: Vertices2Df;
    let rPlot1: Vertices2Df;
    let rPlot2: Vertices2Df;
    let startPoint1: Vertices3Df = null;
    let endPoint1: Vertices3Df = null;
    let startPoint2: Vertices3Df = null;
    let endPoint2: Vertices3Df = null;
    let lineType: number = 0;
    const length: number = this.plotPointList.length - 1;
    this.isThreeDApplicable =
      this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.NX ||
      this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.ThreeD;

    if (
      (this.selectMode === MEASUREMENT_MODE.DEPTH && this.plotPointList.length === 3) ||
      (this.selectMode === MEASUREMENT_MODE.DEPTH &&
        this.plotPointList.length === 2 &&
        this.isDuringMeasure &&
        isMeasure &&
        this.isThreeDApplicable) ||
      (this.selectMode === MEASUREMENT_MODE.AREA && isMeasure && !this.isDuringMeasure)
    ) {
      // 面基準計測の2, 3本目の線
      lPlot1 = new Vertices2Df(this.plotPointList[length].msr_pnt.x, this.plotPointList[length].msr_pnt.y);
      lPlot2 = new Vertices2Df(this.plotPointList[0].msr_pnt.x, this.plotPointList[0].msr_pnt.y);
      rPlot1 = new Vertices2Df(this.plotPointList[length].res_pnt.x, this.plotPointList[length].res_pnt.y);
      rPlot2 = new Vertices2Df(this.plotPointList[0].res_pnt.x, this.plotPointList[0].res_pnt.y);
      const lPlot3 = new Vertices2Df(this.plotPointList[length - 1].msr_pnt.x, this.plotPointList[length - 1].msr_pnt.y);
      const lPlot4 = new Vertices2Df(this.plotPointList[length].msr_pnt.x, this.plotPointList[length].msr_pnt.y);
      const rPlot3 = new Vertices2Df(this.plotPointList[length - 1].res_pnt.x, this.plotPointList[length - 1].res_pnt.y);
      const rPlot4 = new Vertices2Df(this.plotPointList[length].res_pnt.x, this.plotPointList[length].res_pnt.y);

      if (this.isThreeDApplicable) {
        startPoint1 = new Vertices3Df(this.plotPointList[length].pnt_3d.x, this.plotPointList[length].pnt_3d.y, this.plotPointList[length].pnt_3d.z);
        startPoint2 = new Vertices3Df(this.plotPointList[0].pnt_3d.x, this.plotPointList[0].pnt_3d.y, this.plotPointList[0].pnt_3d.z);
        endPoint1 = new Vertices3Df(
          this.plotPointList[length - 1].pnt_3d.x,
          this.plotPointList[length - 1].pnt_3d.y,
          this.plotPointList[length - 1].pnt_3d.z
        );
        endPoint2 = new Vertices3Df(this.plotPointList[length].pnt_3d.x, this.plotPointList[length].pnt_3d.y, this.plotPointList[length].pnt_3d.z);
        if (this.isReady3d) {
          this.setLine3d(startPoint2, endPoint2, MEASUREMENT_MODE.DEPTH, this.getResultId(), true, this.referenceLineColor);
        }
      }

      const lineCoordinateResult: LineCoordinateResult = await this.getLineCoordinatesAsync(
        [lPlot3, lPlot4],
        [rPlot3, rPlot4],
        [startPoint2, endPoint2],
        lineType,
        true
      );
      if (lineCoordinateResult?.requirePushMeasurementResult) {
        this.pushMeasurementResult();
      }
    } else if (!(this.selectMode === MEASUREMENT_MODE.DEPTH && isMeasure)) {
      lPlot1 = new Vertices2Df(this.plotPointList[length - 1].msr_pnt.x, this.plotPointList[length - 1].msr_pnt.y);
      lPlot2 = new Vertices2Df(this.plotPointList[length].msr_pnt.x, this.plotPointList[length].msr_pnt.y);
      rPlot1 = new Vertices2Df(this.plotPointList[length - 1].res_pnt.x, this.plotPointList[length - 1].res_pnt.y);
      rPlot2 = new Vertices2Df(this.plotPointList[length].res_pnt.x, this.plotPointList[length].res_pnt.y);
      if (this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE && this.plotPointList.length === 2) {
        lineType = 1;
      }

      if (this.isThreeDApplicable) {
        startPoint1 = new Vertices3Df(
          this.plotPointList[length - 1].pnt_3d.x,
          this.plotPointList[length - 1].pnt_3d.y,
          this.plotPointList[length - 1].pnt_3d.z
        );
        endPoint1 = new Vertices3Df(this.plotPointList[length].pnt_3d.x, this.plotPointList[length].pnt_3d.y, this.plotPointList[length].pnt_3d.z);
      }
    }

    if (lPlot1 || rPlot1 || lPlot2 || rPlot2) {
      const lineCoordinateResult: LineCoordinateResult = await this.getLineCoordinatesAsync(
        [lPlot1, lPlot2],
        [rPlot1, rPlot2],
        [startPoint1, endPoint1],
        lineType,
        false
      );

      if (this.isThreeDApplicable && this.isReady3d) {
        const color: string = this.get3dLineColor(foot);
        this.setLine3d(startPoint1, endPoint1, this.selectMode, this.getResultId(), this.isStandard(color), color);
      }

      if (lineCoordinateResult?.requirePushMeasurementResult) {
        this.pushMeasurementResult();
      }
    }
  }

  private getLeftCanvasLine(): Line2D {
    const startPoint = this.plotPointList[this.plotPointList.length - 2];
    const endPoint = this.plotPointList[this.plotPointList.length - 1];
    return {
      startPoint: new Vertices2Df(startPoint.msr_pnt.x, startPoint.msr_pnt.y),
      endPoint: new Vertices2Df(endPoint.msr_pnt.x, endPoint.msr_pnt.y)
    };
  }

  private getRightCanvasLine(): Line2D {
    const startPoint = this.plotPointList[this.plotPointList.length - 2];
    const endPoint = this.plotPointList[this.plotPointList.length - 1];
    return {
      startPoint: new Vertices2Df(startPoint.res_pnt.x, startPoint.res_pnt.y),
      endPoint: new Vertices2Df(endPoint.res_pnt.x, endPoint.res_pnt.y)
    };
  }

  private async handleMissingCornerLine(): Promise<LineCoordinateResult | null> {
    const isLineMeasurementPossible = this.plotPointList.length === 4 || this.plotPointList.length === 2;
    if (!isLineMeasurementPossible) {
      return null;
    }
    const leftCanvasLine = this.getLeftCanvasLine();
    const rightCanvasLine = this.getRightCanvasLine();
    const startPoint1 = new Vertices3Df(
      this.plotPointList[this.plotPointList.length - 2].pnt_3d.x,
      this.plotPointList[this.plotPointList.length - 2].pnt_3d.y,
      this.plotPointList[this.plotPointList.length - 2].pnt_3d.z
    );
    const endPoint1 = new Vertices3Df(
      this.plotPointList[this.plotPointList.length - 1].pnt_3d.x,
      this.plotPointList[this.plotPointList.length - 1].pnt_3d.y,
      this.plotPointList[this.plotPointList.length - 1].pnt_3d.z
    );

    if (this.isThreeDApplicable && this.isReady3d) {
      this.setLine3d(startPoint1, endPoint1, this.selectMode, this.getResultId(), true, this.referenceLineColor);
    }

    return await this.getLineCoordinatesAsync(
      [leftCanvasLine.startPoint, leftCanvasLine.endPoint],
      [rightCanvasLine.startPoint, rightCanvasLine.endPoint],
      [startPoint1, endPoint1],
      1,
      false
    );
  }

  /**
   * Obtain the configuration points of the distortion reproduction curve via the ViSOL API,
   * connect the returned configuration points into lines, and render them on the Canvas
   *
   * @param isFirstMeasure
   * @param foot
   * @param threeDFoot
   * @returns The result of the line coordinates.
   */
  private async distortionReproductionCurveAcquisitionAsync(
    isFirstMeasure: boolean,
    foot: any,
    threeDFoot: Vertices3Df
  ): Promise<LineCoordinateResult | null> {
    if (this.selectMode === MEASUREMENT_MODE.MISSING_CORNER) {
      return this.handleMissingCornerLine();
    }

    let lPlot1: Vertices2Df;
    let lPlot2: Vertices2Df;
    let rPlot1: Vertices2Df;
    let rPlot2: Vertices2Df;
    let startPoint1: Vertices3Df = null;
    let endPoint1: Vertices3Df = null;
    let startPoint2: Vertices3Df = null;
    let endPoint2: Vertices3Df = null;
    let result: LineCoordinateResult;
    let lineType: number = 0;
    this.isThreeDApplicable =
      this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.NX ||
      this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.ThreeD;

    // Line segment between the first two points
    if (
      this.selectMode === MEASUREMENT_MODE.DISTANCE ||
      (this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE && this.plotPointList.length === 2) ||
      (this.selectMode === MEASUREMENT_MODE.DEPTH && this.plotPointList.length === 2)
    ) {
      lPlot1 = new Vertices2Df(this.plotPointList[0].msr_pnt.x, this.plotPointList[0].msr_pnt.y);
      lPlot2 = new Vertices2Df(this.plotPointList[1].msr_pnt.x, this.plotPointList[1].msr_pnt.y);
      rPlot1 = new Vertices2Df(this.plotPointList[0].res_pnt.x, this.plotPointList[0].res_pnt.y);
      rPlot2 = new Vertices2Df(this.plotPointList[1].res_pnt.x, this.plotPointList[1].res_pnt.y);
      if (this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE) {
        lineType = 1;
      }

      if (this.isThreeDApplicable) {
        startPoint1 = new Vertices3Df(this.plotPointList[0].pnt_3d.x, this.plotPointList[0].pnt_3d.y, this.plotPointList[0].pnt_3d.z);
        endPoint1 = new Vertices3Df(this.plotPointList[1].pnt_3d.x, this.plotPointList[1].pnt_3d.y, this.plotPointList[1].pnt_3d.z);
      }
    } else if (isFirstMeasure && foot) {
      // Perpendicular line of line reference measurement (first time)
      lPlot1 = new Vertices2Df(foot[0].x, foot[0].y);
      lPlot2 = new Vertices2Df(this.plotPointList[2].msr_pnt.x, this.plotPointList[2].msr_pnt.y);
      rPlot1 = new Vertices2Df(foot[1].x, foot[1].y);
      rPlot2 = new Vertices2Df(this.plotPointList[2].res_pnt.x, this.plotPointList[2].res_pnt.y);
      const point3d = this.isThreeDApplicable
        ? new Vertices3Df(this.plotPointList[2].pnt_3d.x, this.plotPointList[2].pnt_3d.y, this.plotPointList[2].pnt_3d.z)
        : null;
      this.dataset2d(lPlot1, rPlot1, true, point3d);

      if (this.isThreeDApplicable) {
        startPoint1 = new Vertices3Df(this.plotPointList[2].pnt_3d.x, this.plotPointList[2].pnt_3d.y, this.plotPointList[2].pnt_3d.z);
        endPoint1 = new Vertices3Df(threeDFoot.x, threeDFoot.y, threeDFoot.z);
      }
    } else if (!isFirstMeasure && foot) {
      // Perpendicular line of line reference measurement (after first time)
      lPlot1 = new Vertices2Df(foot[0].x, foot[0].y);
      lPlot2 = new Vertices2Df(this.plotPointList[0].msr_pnt.x, this.plotPointList[0].msr_pnt.y);
      rPlot1 = new Vertices2Df(foot[1].x, foot[1].y);
      rPlot2 = new Vertices2Df(this.plotPointList[0].res_pnt.x, this.plotPointList[0].res_pnt.y);
      const point3d = this.isThreeDApplicable
        ? new Vertices3Df(this.plotPointList[0].pnt_3d.x, this.plotPointList[0].pnt_3d.y, this.plotPointList[0].pnt_3d.z)
        : null;
      this.dataset2d(lPlot1, rPlot1, true, point3d);

      if (this.isThreeDApplicable) {
        startPoint1 = new Vertices3Df(threeDFoot.x, threeDFoot.y, threeDFoot.z);
        endPoint1 = new Vertices3Df(this.plotPointList[0].pnt_3d.x, this.plotPointList[0].pnt_3d.y, this.plotPointList[0].pnt_3d.z);
      }
    } else if (this.selectMode === MEASUREMENT_MODE.DEPTH && this.plotPointList.length === 3) {
      // 面基準計測の2, 3本目の線
      lPlot1 = new Vertices2Df(this.plotPointList[2].msr_pnt.x, this.plotPointList[2].msr_pnt.y);
      lPlot2 = new Vertices2Df(this.plotPointList[0].msr_pnt.x, this.plotPointList[0].msr_pnt.y);
      rPlot1 = new Vertices2Df(this.plotPointList[2].res_pnt.x, this.plotPointList[2].res_pnt.y);
      rPlot2 = new Vertices2Df(this.plotPointList[0].res_pnt.x, this.plotPointList[0].res_pnt.y);
      const lPlot3: Vertices2Df = new Vertices2Df(this.plotPointList[1].msr_pnt.x, this.plotPointList[1].msr_pnt.y);
      const lPlot4: Vertices2Df = new Vertices2Df(this.plotPointList[2].msr_pnt.x, this.plotPointList[2].msr_pnt.y);
      const rPlot3: Vertices2Df = new Vertices2Df(this.plotPointList[1].res_pnt.x, this.plotPointList[1].res_pnt.y);
      const rPlot4: Vertices2Df = new Vertices2Df(this.plotPointList[2].res_pnt.x, this.plotPointList[2].res_pnt.y);

      if (this.isThreeDApplicable) {
        startPoint1 = new Vertices3Df(this.plotPointList[2].pnt_3d.x, this.plotPointList[2].pnt_3d.y, this.plotPointList[2].pnt_3d.z);
        startPoint2 = new Vertices3Df(this.plotPointList[0].pnt_3d.x, this.plotPointList[0].pnt_3d.y, this.plotPointList[0].pnt_3d.z);
        endPoint1 = new Vertices3Df(this.plotPointList[1].pnt_3d.x, this.plotPointList[1].pnt_3d.y, this.plotPointList[1].pnt_3d.z);
        endPoint2 = new Vertices3Df(this.plotPointList[2].pnt_3d.x, this.plotPointList[2].pnt_3d.y, this.plotPointList[2].pnt_3d.z);
        if (this.isReady3d) {
          this.setLine3d(startPoint2, endPoint2, MEASUREMENT_MODE.DEPTH, this.getResultId(), true, this.referenceLineColor);
        }
      }

      result = await this.getLineCoordinatesAsync([lPlot3, lPlot4], [rPlot3, rPlot4], [startPoint2, endPoint2], lineType, true);
    } else if (
      this.selectMode === MEASUREMENT_MODE.LINES ||
      (this.selectMode === MEASUREMENT_MODE.AREA &&
        !this.isCrossingLines(
          this.plotPointList[this.plotPointList.length - 2].msr_pnt.x,
          this.plotPointList[this.plotPointList.length - 2].msr_pnt.y,
          this.plotPointList[this.plotPointList.length - 1].msr_pnt.x,
          this.plotPointList[this.plotPointList.length - 1].msr_pnt.y
        ))
    ) {
      lPlot1 = new Vertices2Df(
        this.plotPointList[this.plotPointList.length - 2].msr_pnt.x,
        this.plotPointList[this.plotPointList.length - 2].msr_pnt.y
      );
      lPlot2 = new Vertices2Df(
        this.plotPointList[this.plotPointList.length - 1].msr_pnt.x,
        this.plotPointList[this.plotPointList.length - 1].msr_pnt.y
      );
      rPlot1 = new Vertices2Df(
        this.plotPointList[this.plotPointList.length - 2].res_pnt.x,
        this.plotPointList[this.plotPointList.length - 2].res_pnt.y
      );
      rPlot2 = new Vertices2Df(
        this.plotPointList[this.plotPointList.length - 1].res_pnt.x,
        this.plotPointList[this.plotPointList.length - 1].res_pnt.y
      );

      if (this.isThreeDApplicable) {
        startPoint1 = new Vertices3Df(
          this.plotPointList[this.plotPointList.length - 2].pnt_3d.x,
          this.plotPointList[this.plotPointList.length - 2].pnt_3d.y,
          this.plotPointList[this.plotPointList.length - 2].pnt_3d.z
        );
        endPoint1 = new Vertices3Df(
          this.plotPointList[this.plotPointList.length - 1].pnt_3d.x,
          this.plotPointList[this.plotPointList.length - 1].pnt_3d.y,
          this.plotPointList[this.plotPointList.length - 1].pnt_3d.z
        );
      }
    } else if (
      this.isCrossingLines(
        this.plotPointList[this.plotPointList.length - 2].msr_pnt.x,
        this.plotPointList[this.plotPointList.length - 2].msr_pnt.y,
        this.plotPointList[this.plotPointList.length - 1].msr_pnt.x,
        this.plotPointList[this.plotPointList.length - 1].msr_pnt.y
      )
    ) {
      lPlot1 = new Vertices2Df(
        this.plotPointList[this.plotPointList.length - 2].msr_pnt.x,
        this.plotPointList[this.plotPointList.length - 2].msr_pnt.y
      );
      lPlot2 = new Vertices2Df(
        this.plotPointList[this.plotPointList.length - 1].msr_pnt.x,
        this.plotPointList[this.plotPointList.length - 1].msr_pnt.y
      );
      rPlot1 = new Vertices2Df(
        this.plotPointList[this.plotPointList.length - 2].res_pnt.x,
        this.plotPointList[this.plotPointList.length - 2].res_pnt.y
      );
      rPlot2 = new Vertices2Df(
        this.plotPointList[this.plotPointList.length - 1].res_pnt.x,
        this.plotPointList[this.plotPointList.length - 1].res_pnt.y
      );

      if (this.isThreeDApplicable) {
        startPoint1 = new Vertices3Df(
          this.plotPointList[this.plotPointList.length - 2].pnt_3d.x,
          this.plotPointList[this.plotPointList.length - 2].pnt_3d.y,
          this.plotPointList[this.plotPointList.length - 2].pnt_3d.z
        );
        endPoint1 = new Vertices3Df(
          this.plotPointList[this.plotPointList.length - 1].pnt_3d.x,
          this.plotPointList[this.plotPointList.length - 1].pnt_3d.y,
          this.plotPointList[this.plotPointList.length - 1].pnt_3d.z
        );
      }
    }

    if (lPlot1 || rPlot1 || lPlot2 || rPlot2) {
      result = await this.getLineCoordinatesAsync([lPlot1, lPlot2], [rPlot1, rPlot2], [startPoint1, endPoint1], lineType, false);

      if (this.isThreeDApplicable && this.isReady3d) {
        const color = this.get3dLineColor(foot);
        this.setLine3d(startPoint1, endPoint1, this.selectMode, this.getResultId(), this.isStandard(color), color);
      }
    }
    return result;
  }

  getInspectionImageMeasurementData() {
    this.inspectionImageService
      .getInspectionImageMeasurementData(this.inspectionImage.inspectionId, this.inspectionImage.id)
      .pipe(
        mergeMap((data) =>
          defer(() => {
            return this.loadMeasurementData(data).catch((err) => {
              // Execute when the same API encounters 5 errors during loading measurement
              console.log(err);

              this.selectMode = null;
              this.plotPointList = [];
              this.retentionResult = [];
              this.measurementNumData = [];
              this.referenceSurface = [];
              this.referenceLine = [];
              this.selectData = [];
              this.movePointList = [];
              this.isLoading = false;
              this.isWaitLoading = false;
              this.isLoadMeasurementData = false;
              this.isCanvasLoading = false;

              this.snackBar.open(this.translate.instant("imageRemeasurementEdit.measureError"));
              this.leftCanvas.discardActiveObject();
              this.resizeAfterLoading();
              this.initCanvasAndResult();
              this.emitModifiedData();
              this.oscSpinnerService.hide(true);

              if (this.loadMeasurementPressed) {
                this.loadMeasurementPressed = false;
              }
            });
          })
        )
      )
      .subscribe(
        () => {},
        () => {
          this.isImageInfo = true;
        }
      );
  }

  loadMeasurement() {
    this.getInspectionImageMeasurementData();
    this.loadMeasurementPressed = true;
  }

  /**
   * Obtain and generate measurement information for the inspection image
   * Control the screen while retrieving
   */
  async getAndGenerationOfMeasureInfo(isReset: boolean): Promise<void> {
    this.addResizeEvent();
    this.isLoading = true;
    this.oscSpinnerService.show(true);
    try {
      await Promise.all([
        this.measurementService
          .getAndGenerationOfMeasureInfo(this.inspectionImage.inspectionId, this.inspectionImage.id)
          .then((item) => {
            this.measurementInfoData = item;
            if (this.measurementInfoData.model3DData == null) {
              this.get3DModelData();
            }
          })
          .catch(() => {
            this.isReadyMeasurement = false;
          }),
        this.loadImage()
      ]).catch(() => {
        this.oscSpinnerService.hide(true);
        this.resizeAfterLoading();
      });
    } finally {
      if (isReset) {
        this.getInspectionImageMeasurementData();
        this.loadMeasurementPressed = true;
      } else {
        this.isImageInfo = true;
        this.oscSpinnerService.hide(true);
        this.resizeAfterLoading();
      }
    }
  }

  /**
   * Acquisition of data necessary for displaying 3D objects
   */
  async get3DModelData(): Promise<void> {
    this.oscSpinnerService.show(true);
    this.isLoading = true;
    this.oscSpinnerService.displayMessage(this.translate.instant("imageRemeasurementEdit.loadingMessageGenerate3DModelProcess"));
    await this.measurementService
      .get3DModelData(this.inspectionImage.inspectionId, this.inspectionImage.id)
      .then((item) => {
        this.measurementInfoData.model3DData = item;
      })
      .catch((error) => {
        this.oscSpinnerService.hide(true);
        console.log(error);
        this.resizeAfterLoading();
      });
  }

  /**
   * Obtaining an image to display as a texture for a 3D object
   */
  async loadUvImage(): Promise<void> {
    await this.measurementService
      .getImageBinaryUrl(this.inspectionImage.inspectionId, this.inspectionImage.id)
      .then(async (image) => {
        this.uvImage = image;
        // Read image from URL in base64 format
        return await this.blobService.getBase64ImageFromUrl(this.uvImage);
      })
      .then((base64) => {
        this.uvImgSrcBase64 = base64;
        this.create3DObject();
      })
      .catch((err) => {
        this.oscSpinnerService.hide(true);
        console.log(err);
        this.resizeAfterLoading();
      });
  }

  async loadImage(): Promise<void> {
    try {
      // Retrieve the image URL based on the inspection ID and image ID
      this.imageSrc = await this.inspectionImageService.getImageBinaryUrl(
        this.inspectionImage.inspectionId,
        this.inspectionImage.id,
        USE_IMAGE_TYPE.ORIGINAL
      );

      const base64 = await this.blobService.getBase64ImageFromUrl(this.imageSrc);

      await this.setupImageOnCanvas(base64);
    } catch (err) {
      console.error("Failed to load image:", err);
    }
  }

  private async setupImageOnCanvas(base64: string): Promise<void> {
    const image = new Image();
    this.imgSrcBase64 = base64;
    image.src = base64;
    image.onload = () => {
      this.setupCanvas(image);
      this.setCanvasEvent();
    };
  }

  private setupLeftCanvas(image: HTMLImageElement): void {
    const leftCanvasConfig = {
      width: (image.width * this.scale) / 2,
      height: image.height * this.scale,
      border: "solid"
    };

    const leftCanvasImage = new FabricImage(image, {
      originX: "center",
      originY: "center",
      left: leftCanvasConfig.width,
      top: leftCanvasConfig.height / 2,
      scaleX: this.scale,
      scaleY: this.scale,
      selectable: false,
      evented: true,
      hasControls: false,
      lockMovementX: true,
      lockMovementY: true,
      lockScalingX: true,
      lockScalingY: true
    });

    // Configure the left canvas for operation
    this.leftCanvas = new Canvas("leftCanvas", leftCanvasConfig);
    this.leftCanvas.preserveObjectStacking = true;
    this.leftCanvas.freeDrawingBrush = new PencilBrush(this.leftCanvas);
    this.leftCanvas.freeDrawingBrush.width = 3;
    this.leftCanvas.defaultCursor = "crosshair";
    this.leftCanvas.hoverCursor = "default";
    this.leftCanvas.add(leftCanvasImage);
  }

  private setupRightCanvas(image: HTMLImageElement): void {
    const rightCanvasConfig = {
      width: (image.width * this.scale) / 2,
      height: image.height * this.scale,
      border: "solid"
    };

    const rightCanvasImage = new FabricImage(image, {
      originX: "center",
      originY: "center",
      left: 0,
      top: rightCanvasConfig.height / 2,
      scaleX: this.scale,
      scaleY: this.scale,
      selectable: false,
      evented: false,
      hasControls: false,
      lockMovementX: true,
      lockMovementY: true,
      lockScalingX: true,
      lockScalingY: true
    });

    // Configure the right canvas for operation
    this.rightCanvas = new Canvas("rightCanvas", rightCanvasConfig);
    this.rightCanvas.preserveObjectStacking = true;
    this.rightCanvas.freeDrawingBrush = new PencilBrush(this.rightCanvas);
    this.rightCanvas.freeDrawingBrush.width = 3;
    this.rightCanvas.hoverCursor = "default";
    this.rightCanvas.add(rightCanvasImage);
  }

  private setupDummyCanvas(image: HTMLImageElement): void {
    const dummyCanvasConfig = {
      width: image.width,
      height: image.height,
      border: "solid"
    };

    const dummyCanvasImage = new FabricImage(image, {
      originX: "center",
      originY: "center",
      left: dummyCanvasConfig.width / 2,
      top: dummyCanvasConfig.height / 2,
      scaleX: 1,
      scaleY: 1,
      selectable: false,
      evented: false,
      hasControls: false,
      lockMovementX: true,
      lockMovementY: true,
      lockScalingX: true,
      lockScalingY: true
    });

    // Configure the canvas for saving
    // Since only events on the dummyCanvas are monitored, execute dummyCanvas last
    this.dummyCanvas = new Canvas("dummyCanvas", dummyCanvasConfig);
    this.dummyCanvas.preserveObjectStacking = true;
    this.dummyCanvas.freeDrawingBrush = new PencilBrush(this.dummyCanvas);
    this.dummyCanvas.freeDrawingBrush.width = 3;
    this.dummyCanvas.add(dummyCanvasImage);
  }

  /** Canvas settings */
  setupCanvas(image: HTMLImageElement): void {
    this.mainImg = image;
    this.scale = this.getScale();
    this.setupLeftCanvas(image);
    this.setupRightCanvas(image);
    this.setupDummyCanvas(image);
    this.initDataJson = JSON.parse(this.getPreciseJson());
    this.setMeasurementResult();
    this.emitModifiedData();

    this.isCanvasLoading = false;
  }

  // Synchronize scrolling of the left and right canvases
  syncScrollbarR() {
    const leftScrollber = document.getElementById("left-canvas-area");
    const rightScrollber = document.getElementById("right-canvas-area");
    leftScrollber.scrollTop = rightScrollber.scrollTop;
    leftScrollber.scrollLeft = rightScrollber.scrollLeft;
  }

  syncScrollbarL() {
    const leftScrollber = document.getElementById("left-canvas-area");
    const rightScrollber = document.getElementById("right-canvas-area");
    rightScrollber.scrollTop = leftScrollber.scrollTop;
    rightScrollber.scrollLeft = leftScrollber.scrollLeft;
  }

  setCanvasEvent() {
    this.leftCanvas.selection = false;
    this.rightCanvas.selection = false;

    // setCanvasEvent() is re-invoked during canvas restoration; Fabric's on() appends, so drop prior handlers first.
    this.dummyCanvas.off("object:added");
    this.dummyCanvas.off("object:modified");
    this.dummyCanvas.off("selection:created");
    this.dummyCanvas.off("selection:updated");
    this.dummyCanvas.off("selection:cleared");
    this.leftCanvas.off("mouse:over");
    this.leftCanvas.off("mouse:out");
    this.leftCanvas.off("selection:created");
    this.leftCanvas.off("object:modified");
    this.leftCanvas.off("mouse:down");

    this.dummyCanvas.on("object:added", () => this.emitModifiedData());
    this.dummyCanvas.on("object:modified", () => this.emitModifiedData());
    this.dummyCanvas.on("selection:created", () => this.setObjectEditor());
    this.dummyCanvas.on("selection:updated", () => this.setObjectEditor());
    this.dummyCanvas.on("selection:cleared", () => (this.firstActiveObjIndex = -1));

    this.leftCanvas.on("mouse:over", (e) => this.setSelectMode(e, true));
    this.leftCanvas.on("mouse:out", (e) => this.setSelectMode(e, false));
    this.leftCanvas.on("selection:created", () => this.keepAnnotationAndResult());
    this.leftCanvas.on("object:modified", () => void this.moveObject());
    this.leftCanvas.on("mouse:down", (event) => this.onAddPoint(event.pointer.x, event.pointer.y));

    // Spinner control
    this.isLoadImage = true;
    if (this.isImageInfo) {
      this.oscSpinnerService.hide(true);
      this.resizeAfterLoading();
    }
  }

  removeDummyCanvasEvent() {
    this.dummyCanvas.removeListeners();
  }

  private emitModifiedData() {
    const canvasJson = this.dummyCanvas.toDatalessJSON(["data"]);
    // Compare with the initial state
    if (JSON.stringify(canvasJson) !== JSON.stringify(this.initDataJson)) {
      // If changes have been made, update
      const jsonData = this.getPreciseJson();
      const modifiedData = JSON.parse(jsonData);
      if (modifiedData.objects[0] && !modifiedData.objects[0].data) {
        modifiedData.objects[0].data = { isNewSpecification: true }; // Whether there was a measurement from v2.0.20 onward
      }
      const base64Data = this.dummyCanvas.toDataURL({
        format: "jpeg",
        quality: 1,
        multiplier: 1
      });

      const blob = this.toBlob(base64Data);
      const result = new AnnotationEmitData(blob, null, modifiedData.objects, this.initDataJson, this.isDisabled());
      this.modified.emit(result);
    } else {
      // If no changes have been made, update the object with null
      const result = new AnnotationEmitData(null, null, null, this.initDataJson, this.isDisabled());
      this.modified.emit(result);
    }
  }

  private toBlob(base64: string, type: string = "image/jpeg"): Blob {
    // Convert from Base64 to binary
    const bin = atob(base64.replace(/^.*,/, ""));
    const buffer = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) {
      buffer[i] = bin.charCodeAt(i);
    }

    // Create a Blob
    return new Blob([buffer.buffer], { type: type });
  }

  // Increase the accuracy of the background image in JSON
  // Without this process, the decimal precision of the scale will only be up to the second place
  private getPreciseJson() {
    let scale = null;

    this.dummyCanvas._objects.some((obj, i) => {
      if (obj.isType("image") && i === 0) {
        scale = obj.scaleY;
        return true;
      }
      return false;
    });

    const json = this.dummyCanvas.toDatalessJSON(["data"]);
    const data = JSON.parse(JSON.stringify(json));
    data.objects.forEach((obj, i) => {
      if (obj.type.toLowerCase() === "image") {
        obj.scaleX = obj.scaleY = scale;
        obj.left = this.dummyCanvas.width / 2;
        obj.top = this.dummyCanvas.height / 2;
      }
    });

    return JSON.stringify(data);
  }

  private setObjectEditor() {
    const canvasObjects = this.leftCanvas._objects;
    const activeObjects = this.leftCanvas.getActiveObjects();
    let activeObj: FabricObject;

    if (this.firstActiveObjIndex > 0) {
      const beforeObj = canvasObjects[this.firstActiveObjIndex];

      if (activeObjects.indexOf(beforeObj) > 0) {
        // If the previously selected object is active, use it
        activeObj = beforeObj;
      } else {
        // If the previously selected object is inactive, use the first active one instead
        activeObj = activeObjects[0];
        this.firstActiveObjIndex = canvasObjects.indexOf(activeObj);
      }
    } else {
      // For the first selection, use the first of the selected objects
      activeObj = activeObjects[0];
      this.firstActiveObjIndex = canvasObjects.indexOf(activeObj);
    }
  }

  // Apply annotations to the image
  private loadJsonToCanvas(json: any, isAddHistory: boolean = false, isResize: boolean, canvas: string) {
    this.isCanvasLoading = true;
    const jsonData = isResize ? JSON.parse(JSON.stringify(json)) : this.getScaleCorrectionJsonData(json);
    const scale = this.scale;

    if (canvas === this.LEFTCANVAS) {
      this.leftCanvas.discardActiveObject();
      this.leftCanvas.isDrawingMode = false;
      jsonData.objects.filter((obj) => {
        if (obj.type.toLowerCase() === "image") {
          obj.scaleX = obj.scaleY = this.scale;
          obj.left = this.leftCanvas.width;
          obj.top = this.leftCanvas.height / 2;
          return true;
        } else {
          obj.left = obj.left * scale;
          obj.top = obj.top * scale;
          obj.fontSize = obj.fontSize * scale;
          obj.strokeWidth = obj.strokeWidth * scale;
          obj.x1 = obj.x1 * scale;
          obj.x2 = obj.x2 * scale;
          obj.y1 = obj.y1 * scale;
          obj.y2 = obj.y2 * scale;
          return obj.left <= this.mainImg.width / 2;
        }
      });
      this.leftCanvas.loadFromJSON(jsonData, () => {
        this.leftCanvas._objects.forEach((obj: any) => {
          if (obj.type === "text" && (obj.text === "×" || obj.text === "◇")) {
            obj.set({ selectable: true, hasControls: false });
          } else {
            obj.set({ selectable: false });
          }
        });
      });
    } else if (canvas === this.RIGHTCANVAS) {
      jsonData.objects.filter((obj) => {
        if (obj.type.toLowerCase() === "image") {
          obj.scaleX = obj.scaleY = this.scale;
          obj.left = this.rightCanvas.width;
          obj.top = this.rightCanvas.height / 2;
          return true;
        } else {
          obj.left = obj.left * scale;
          obj.top = obj.top * scale;
          obj.fontSize = obj.fontSize * scale;
          obj.strokeWidth = obj.strokeWidth * scale;
          obj.x1 = obj.x1 * scale;
          obj.x2 = obj.x2 * scale;
          obj.y1 = obj.y1 * scale;
          obj.y2 = obj.y2 * scale;
          return obj.left > this.mainImg.width / 2;
        }
      });
      this.rightCanvas.loadFromJSON(jsonData, () => {
        const image = this.rightCanvas.getObjects().find((_) => _.type === "image") as FabricImage;
        const obj = this.rightCanvas.getObjects().filter((_) => _.type !== "image");

        // Set 'left' to display the right half of the image and reflect changes with renderAll
        image?.set({ selectable: false, left: 0 });

        // Adjust object coordinates for the left canvas
        obj.forEach((item) => {
          item.set({ left: item.left - this.halfMainImgW, selectable: false });
          item.setCoords();
        });

        this.rightCanvas.renderAll();
      });
    } else {
      this.removeDummyCanvasEvent();
      jsonData.objects.forEach((obj) => {
        if (obj.type.toLowerCase() === "image") {
          obj.scaleX = obj.scaleY = 1;
          obj.left = this.dummyCanvas.width / 2;
          obj.top = this.dummyCanvas.height / 2;
        }
      });
      this.dummyCanvas.loadFromJSON(jsonData, () => {
        if (isAddHistory) {
          this.setCanvasEvent();
          this.emitModifiedData();
        }
        if (!isResize) {
          this.initDataJson = JSON.parse(this.getPreciseJson());
        }
        this.emitModifiedData();
      });
    }

    this.collectRenderAll();
    this.emitModifiedData();
    this.isCanvasLoading = false;
  }

  // Apply annotations to the image
  setMeasurementResult() {
    if (this.annotationObjects?.measurementItems) {
      let isLoop = true;
      const measureItems = this.annotationObjects?.measurementItems;
      let unit = measureItems[1] && measureItems[1].data && measureItems[1].data.unit ? measureItems[1].data.unit : this.unitSelected;

      do {
        const plotPointList = [];
        let id;
        let mode;
        let result;
        const imageWidth = this.annotationObjects?.measurementItems.find((obj) => obj.type.toLowerCase() === "image").width;

        if (this.measurementResult.length === 0 && measureItems[1]) {
          id = measureItems[1].data.id;
          mode = measureItems[1].data.mode;
          result = measureItems[1].data.result;
          unit = measureItems[1].data.unit ? measureItems[1].data.unit : this.unitSelected;
          measureItems.forEach((obj) => {
            if (obj.left <= imageWidth / 2 && obj.type !== "image" && obj.data.id === id) {
              const mResult = new PlotResult(obj.left / this.scale, obj.top / this.scale);
              plotPointList.push(mResult);
            }
          });
        } else {
          const ids = measureItems.find(
            (obj) => obj.type.toLowerCase() !== "image" && obj.data.id && !this.measurementResult.some((item) => item.id === obj.data.id)
          );
          id = ids?.data.id;
          mode = measureItems.find((obj) => obj.type.toLowerCase() !== "image" && obj.data.id === id)?.data.mode;
          result = measureItems.find((obj) => obj.type.toLowerCase() !== "image" && obj.data.id === id)?.data.result;
          measureItems.forEach((obj) => {
            if (obj.left <= imageWidth / 2 && obj.type.toLowerCase() !== "image" && obj.data.id === id) {
              const mResult = new PlotResult(obj.left / this.scale, obj.top / this.scale);
              plotPointList.push(mResult);
            }
          });
        }

        if (id) {
          const measureResult = MeasurementResultFactory.createMeasurementResult(
            new PlotResultGroup(id, mode, result.length, result.objectDistance, result.area, result.latestDistance, plotPointList)
          );
          this.measurementResult.push(measureResult);
        } else {
          isLoop = false;
        }
      } while (isLoop);
      this.measurementResult = this.measurementResult.filter((item) => item);
      this.unitSelected = unit;
      this.cd.detectChanges();
      if (this.measurementResult.length > 0) {
        this.selectResult = this.measurementResult[this.measurementResult.length - 1];
      }

      // spinner control
      this.isLoadImage = true;
      if (this.isImageInfo) {
        this.oscSpinnerService.hide(true);
        this.resizeAfterLoading();
      }

      const existingMeasurementData = JSON.stringify({ objects: this.annotationObjects?.measurementItems });
      this.redrawMeasurementObjects(this.LEFTCANVAS, existingMeasurementData);
      this.redrawMeasurementObjects(this.RIGHTCANVAS, existingMeasurementData);
      this.redrawMeasurementObjects(this.DUMMYCANVAS, existingMeasurementData);
    }
  }

  set3dObject(item: any) {
    if (item.type.toLowerCase() === "text") {
      this.dataset3d(item.data.coordinate, item.data.mode, item.data.id, item.data.isStandard, item.fill);
    } else if (item.type.toLowerCase() === "line") {
      this.setLine3d(item.data.startEnd[0], item.data.startEnd[1], item.data.mode, item.data.id, item.data.isStandard, item.stroke);
    }
  }

  changeMagnification(magnification: number = null) {
    const scale = magnification ? magnification : this.imageMagnification;
    this.scaleSetting(scale);
    this.imageMagnification = scale;
  }

  changeUnit() {
    this.unitSelected = this.unitSelected === this.units[0] ? this.units[1] : this.units[0];
    // The reports show each measurement in the unit stored on its items, so the ones already drawn take the new unit too.
    this.dummyCanvas?._objects.forEach((obj) => {
      const data = obj.get("data");
      if (data?.unit) {
        obj.set("data", { ...data, unit: this.unitSelected });
      }
    });
    this.emitModifiedData();
  }

  getUnit(isArea: boolean = false): string {
    if (isArea) {
      return " " + this.unitSelected + "^2";
    } else {
      return " " + this.unitSelected;
    }
  }

  // Change image/canvas size according to selection magnification
  scaleSetting(magnification: number) {
    this.scale = this.getScale();
    document.getElementById("canvas-area-div").style.transform = `scale(${magnification})`;
  }

  private addAreaMeasurementResult(id: string): Promise<any> | undefined {
    const isAreaClosed =
      this.plotPointList &&
      this.plotPointList.length >= 4 &&
      JSON.stringify(this.plotPointList[0]) === JSON.stringify(this.plotPointList[this.plotPointList.length - 1]);
    const isAreaMeasurementPossible = isAreaClosed || this.plotPointList.length === this.maxPoints;

    if (!isAreaMeasurementPossible) {
      return;
    }

    if (this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.NX) {
      const pointList = new AreaLinesNX();
      const pointLists = [];
      this.plotPointList.forEach((item) => {
        pointLists.push(item);
      });

      pointList.pntCount = this.plotPointList.length;
      pointList.pointSets = pointLists;

      return this.measurementService
        .getAreaMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, pointList)
        .then((item) => {
          item = item as AreaMeasureData_NX_WithReturnCode;
          this.retentionResult.push(
            new PlotResultGroup(
              id,
              this.selectMode,
              item.areaResult.perimeter,
              item.areaResult.pntset_meas[this.plotPointList.length - 2].pnt_3d.z,
              item.areaResult.area,
              null,
              this.plotPointList
            )
          );
          this.isApiError = this.isApiError ? true : item.returnCode !== 0;
          return item.returnCode === 0;
        })
        .catch((err) => {
          this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError"));
          return false;
        });
    } else if (this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.IV7) {
      const pointList = new AreaLinesIV7();
      const pointLists = [];
      this.plotPointList.forEach((item) => {
        pointLists.push(item);
      });

      pointList.pntCount = this.plotPointList.length;
      pointList.pointSets = pointLists;

      return Promise.all([
        this.measurementService.getAreaMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, pointList),
        this.measurementService.getOverallLengthMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, pointList)
      ])
        .then((item) => {
          const linesResult = (item[1] as LinesMeasureData_IV7_WithReturnCode).linesResult;
          this.retentionResult.push(
            new PlotResultGroup(
              id,
              this.selectMode,
              linesResult.length,
              item[0].areaResult.pntset_meas[this.plotPointList.length - 1].pnt_3d.z,
              item[0].areaResult.area,
              null,
              this.plotPointList
            )
          );
          this.isApiError = this.isApiError ? true : item[0].returnCode !== 0 || item[1].returnCode !== 0;
          return item[0].returnCode === 0 && item[1].returnCode === 0;
        })
        .catch((err) => {
          this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError"));
          return false;
        });
    } else if (this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.ThreeD) {
      const pointList = new AreaLines3D();
      const pointLists = [];
      this.plotPointList.forEach((item) => {
        pointLists.push(item);
      });

      pointList.pntCount = this.plotPointList.length;
      pointList.pointSets = pointLists;

      return this.measurementService
        .getAreaTotalLengthMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, pointList)
        .then((item) => {
          this.retentionResult.push(
            new PlotResultGroup(
              id,
              this.selectMode,
              item.areaLinesResult.length,
              item.areaLinesResult.pntset_meas[this.plotPointList.length - 2].pnt_3d.z,
              item.areaLinesResult.area,
              null,
              this.plotPointList
            )
          );
          this.isApiError = this.isApiError ? true : item.returnCode !== 0;
          return item.returnCode === 0;
        })
        .catch((err) => {
          this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError"));
          return false;
        });
    }
  }

  private addLinesMeasurementResult(id: string): Promise<any> | undefined {
    // After the first point we start drawing lines between the new point and the previous one.
    const isLinesMeasurementPossible = this.plotPointList.length > 1;

    if (!isLinesMeasurementPossible) {
      return;
    }

    if (this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.IV7) {
      const pointList = new AreaLinesIV7();
      const pointLists = [];
      this.plotPointList.forEach((item) => {
        pointLists.push(item);
      });
      pointList.pntCount = this.plotPointList.length;
      pointList.pointSets = pointLists;

      return Promise.all([
        this.measurementService.getOverallLengthMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, pointList),
        this.measurementService.getTwoPointsMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, [
          this.plotPointList[this.plotPointList.length - 1],
          this.plotPointList[this.plotPointList.length - 2]
        ])
      ])
        .then((item) => {
          const linesResult = (item[0] as LinesMeasureData_IV7_WithReturnCode).linesResult;
          this.retentionResult.push(
            new PlotResultGroup(
              id,
              this.selectMode,
              linesResult.length,
              linesResult.pntset_meas[this.plotPointList.length - 1].pnt_3d.z,
              null,
              item[1].distanceResult.distance,
              this.plotPointList
            )
          );
          this.isApiError = this.isApiError ? true : item[0].returnCode !== 0 || item[1].returnCode !== 0;
          return item[0].returnCode === 0 && item[1].returnCode === 0;
        })
        .catch((err) => {
          this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError"));
          return false;
        });
    } else if (this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.NX) {
      const pointList = new AreaLinesNX();
      const pointLists = [];
      this.plotPointList.forEach((item) => {
        pointLists.push(item);
      });
      pointList.pntCount = this.plotPointList.length;
      pointList.pointSets = pointLists;

      return Promise.all([
        this.measurementService.getOverallLengthMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, pointList),
        this.measurementService.getTwoPointsMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, [
          this.plotPointList[this.plotPointList.length - 1],
          this.plotPointList[this.plotPointList.length - 2]
        ])
      ])
        .then((item) => {
          const linesResult = (item[0] as LinesMeasureData_NX_WithReturnCode).linesResult;
          this.retentionResult.push(
            new PlotResultGroup(
              id,
              this.selectMode,
              linesResult.length,
              linesResult.pntset_meas[this.plotPointList.length - 1].pnt_3d.z,
              null,
              item[1].distanceResult.distance,
              this.plotPointList
            )
          );
          this.isApiError = this.isApiError ? true : item[0].returnCode !== 0 || item[1].returnCode !== 0;
          return item[0].returnCode === 0 && item[1].returnCode === 0;
        })
        .catch((err) => {
          this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError"));
          return false;
        });
    } else if (this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.ThreeD) {
      const pointList = new AreaLines3D();
      const pointLists = [];
      this.plotPointList.forEach((item) => {
        pointLists.push(item);
      });
      pointList.pntCount = this.plotPointList.length;
      pointList.pointSets = pointLists;

      this.measurementService
        .getTwoPointsMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, [
          this.plotPointList[this.plotPointList.length - 1],
          this.plotPointList[this.plotPointList.length - 2]
        ])
        .then((item) => {
          const currentLength = this.retentionResult.filter((r) => r.id === id).reduce((sum, r) => sum + r.latestDistance, 0);
          const totalLength = currentLength + item.distanceResult.distance;
          this.retentionResult.push(
            new PlotResultGroup(
              id,
              this.selectMode,
              totalLength,
              item.distanceResult.pntset_meas[1].pnt_3d.z,
              null,
              item.distanceResult.distance,
              this.plotPointList
            )
          );
          this.isApiError = this.isApiError ? true : item.returnCode !== 0;
          return item.returnCode === 0;
        })
        .catch((err) => {
          this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError"));
          return false;
        });
    }
  }

  private addDepthMeasurementResult(id: string): Promise<any> | undefined {
    const isDepthMeasurementPossible =
      this.plotPointList.length === this.maxPoints || (this.isSurfaceReferenceMeasure() && this.plotPointList.length === 1);
    if (!isDepthMeasurementPossible) {
      return;
    }

    let pointList = [];
    if (this.isSurfaceReferenceMeasure()) {
      // For the second and subsequent measurements, use the retained reference line.
      pointList.push(this.referenceSurface[0]);
      pointList.push(this.referenceSurface[1]);
      pointList.push(this.referenceSurface[2]);
      pointList.push(this.plotPointList[0]);
    } else {
      pointList = this.plotPointList;
    }

    return this.measurementService
      .getSurfaceReferenceMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, pointList, this.measurementInfoData.model3DData)
      .then((item) => {
        // Maintain a baseline
        if (!this.isSurfaceReferenceMeasure()) {
          this.referenceSurface = this.plotPointList;
        }

        this.retentionResult.push(
          new PlotResultGroup(id, MEASUREMENT_MODE.DEPTH, item.depthResult.distance, item.depthResult.pntset_meas.pnt_3d.z, null, null, pointList)
        );
        this.isApiError = this.isApiError ? true : item.returnCode !== 0;
        return item.returnCode === 0;
      })
      .catch((err) => {
        this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError"));
        return false;
      });
  }

  private addPointToLineMeasurementResult(id: string): Promise<any> | undefined {
    this.isCurveError = false;

    if (this.plotPointList.length === this.maxPoints) {
      let pointList = [];
      if (this.isLineReferenceMeasure()) {
        // For the second and subsequent measurements, use the retained reference line.
        pointList.push(this.referenceLine[0]);
        pointList.push(this.referenceLine[1]);
        pointList.push(this.plotPointList[0]);
      } else {
        pointList = this.plotPointList;
      }

      return this.measurementService
        .getLineReferenceMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, pointList)
        .then(async (item) => {
          // Maintain a baseline
          if (!this.isLineReferenceMeasure()) {
            this.referenceLine = this.plotPointList;
          }
          const result = item as PointToLineMeasureData_WithReturnCode;
          const res = result.pointToLineResult;
          if (this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.NX) {
            this.isCurveError = await this.handleDistortionReproductionCurveAsync(res, id, this.isCurveError, res.pntset_meas);
          } else if (this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.IV7) {
            const curve: LineCoordinateResult = await this.distortionReproductionCurveAcquisitionAsync(
              !this.isLineReferenceMeasure(),
              res.foot,
              null
            );
            this.retentionResult.push(
              new PlotResultGroup(id, MEASUREMENT_MODE.POINT_TO_LINE, res.distance, res.pntset_meas.pnt_3d.z, null, null, this.plotPointList)
            );
            this.isCurveError = this.isCurveError
              ? this.isCurveError
              : curve.PolylineDataItems[0].returnCode !== 0 || curve.PolylineDataItems[1].returnCode !== 0;
            this.isApiError = this.isApiError ? true : this.isCurveError;
            if (curve.requirePushMeasurementResult) {
              this.pushMeasurementResult();
            }
          } else {
            this.isCurveError = await this.handleDistortionReproductionCurveAsync(res, id, this.isCurveError, res.pntset_ref[0]);
          }
          return item.returnCode === 0;
        })
        .catch((err) => {
          this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError"));
          return false;
        });
    } else if (this.isLineReferenceMeasure() && this.plotPointList.length === 1) {
      let pointList = [];
      if (this.isLineReferenceMeasure()) {
        // For subsequent measurements, use the retained reference line
        pointList.push(this.referenceLine[0]);
        pointList.push(this.referenceLine[1]);
        pointList.push(this.plotPointList[0]);
      } else {
        pointList = this.plotPointList;
      }

      return this.measurementService
        .getLineReferenceMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, pointList)
        .then(async (item) => {
          // Retain the reference line
          if (!this.isLineReferenceMeasure()) {
            this.referenceLine = this.plotPointList;
          }
          return this.processMeasurement(item, id);
        })
        .catch((err) => {
          this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError"));
          return false;
        });
    }

    return;
  }

  private addDistanceMeasurementResult(id: string): Promise<any> | undefined {
    const isDistanceMeasurementPossible = this.plotPointList.length === this.maxPoints;
    if (!isDistanceMeasurementPossible) {
      return;
    }

    return this.measurementService
      .getTwoPointsMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, this.plotPointList)
      .then((item) => {
        this.retentionResult.push(
          new PlotResultGroup(
            id,
            MEASUREMENT_MODE.DISTANCE,
            item.distanceResult.distance,
            item.distanceResult.pntset_meas[0].pnt_3d.z,
            null,
            null,
            this.plotPointList
          )
        );
        this.isApiError = this.isApiError ? true : item.returnCode !== 0;
        return item.returnCode === 0;
      })
      .catch((err) => {
        this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError"));
        return false;
      });
  }

  private addMissingCornerMeasurementResult(id: string): Promise<any> | undefined {
    const isMissingCornerMeasurementPossible = this.plotPointList.length === this.maxPoints;
    if (!isMissingCornerMeasurementPossible) {
      return;
    }
    return this.measurementService
      .getMissingCornerResult(this.inspectionImage.inspectionId, this.inspectionImage.id, this.plotPointList, this.measurementInfoData.model3DData)
      .then((result) => {
        this.measurementResult.push(MeasurementResultFactory.createMissingCornerResult(result.result, id));
        this.drawMissingCornerResult2D(result.result.resultPoints, id);
        this.drawMissingCornerResult3D(result.result.resultPoints.map((point) => point.pnt_3d));
        return result.returnCode === 0;
      })
      .catch((err) => {
        this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError"));
      });
  }

  private async addMeasurementResult(): Promise<void> {
    const id: string = this.getResultId();
    let measurementRequestTask: Promise<any>;

    switch (this.selectMode) {
      case MEASUREMENT_MODE.AREA:
        measurementRequestTask = this.addAreaMeasurementResult(id);
        break;
      case MEASUREMENT_MODE.LINES:
        measurementRequestTask = this.addLinesMeasurementResult(id);
        break;
      case MEASUREMENT_MODE.DEPTH:
        measurementRequestTask = this.addDepthMeasurementResult(id);
        break;
      case MEASUREMENT_MODE.POINT_TO_LINE:
        measurementRequestTask = this.addPointToLineMeasurementResult(id);
        break;
      case MEASUREMENT_MODE.DISTANCE:
        measurementRequestTask = this.addDistanceMeasurementResult(id);
        break;
      case MEASUREMENT_MODE.MISSING_CORNER:
        measurementRequestTask = this.addMissingCornerMeasurementResult(id);
    }

    if (measurementRequestTask === undefined) {
      // Get line
      if (this.plotPointList.length >= 2) {
        const curve: LineCoordinateResult = await this.distortionReproductionCurveAcquisitionAsync(!this.isLineReferenceMeasure(), null, null);
        if (curve === null) {
          return null;
        }
        this.isCurveError = this.isCurveError
          ? this.isCurveError
          : curve.PolylineDataItems[0].returnCode !== 0 || curve.PolylineDataItems[1].returnCode !== 0;
        this.isApiError = this.isApiError ? true : this.isCurveError;
        if (curve.requirePushMeasurementResult) {
          this.pushMeasurementResult();
        }
      }
      if (this.isCurveError) {
        this.rollBackMeasurement("Measurement Error", this.translate.instant("imageRemeasurementEdit.measureError"));
      }
      return null;
    }

    return await measurementRequestTask.then(async (isSuccess) => {
      if (isSuccess && !this.isCurveError && !this.isApiError) {
        if (this.selectMode === MEASUREMENT_MODE.DEPTH) {
          // 面基準計測の場合
          this.measurementResult.push(MeasurementResultFactory.createMeasurementResult(this.retentionResult[0]));
          this.updateObjectInfoOnCanvas(
            this.retentionResult[0].length,
            this.retentionResult[0].objectDistance,
            this.retentionResult[0].id,
            null,
            null
          );
          this.retentionResult = [];
          this.plotPointList = [];
          this.selectResult = this.measurementResult[this.measurementResult.length - 1];
          this.emitModifiedData();
          this.oscSpinnerService.hide(true);
          this.resizeAfterLoading();
        } else {
          this.emitModifiedData();
        }

        // Get Line
        if (this.plotPointList.length >= 2) {
          const curve: LineCoordinateResult = await this.distortionReproductionCurveAcquisitionAsync(!this.isLineReferenceMeasure(), null, null);
          if (curve.PolylineDataItems[0].returnCode !== 0 || curve.PolylineDataItems[1].returnCode !== 0) {
            this.isApiError = true;
            this.rollBackMeasurement("Measurement Error", this.translate.instant("imageRemeasurementEdit.measureError"));
          } else if (curve.requirePushMeasurementResult) {
            this.pushMeasurementResult();
          }
        }
      } else {
        this.rollBackMeasurement("Measurement Error", this.translate.instant("imageRemeasurementEdit.measureError"));
      }

      if (this.selectMode === MEASUREMENT_MODE.MISSING_CORNER) {
        this.plotPointList = [];
      }
    });
  }

  async processMeasurement(item, id) {
    switch (this.measurementService.getMeasurementType) {
      case MEASUREMENT_IMAGE_MODE.NX:
        return this.handleNXMode(item, id);

      case MEASUREMENT_IMAGE_MODE.IV7:
        return this.handleIV7Mode(item, id);

      default:
        return this.handle3DMode(item, id);
    }
  }

  private async handleNXMode(item, id) {
    const result: PointToLineMeasureData_NX_WithReturnCode = item as PointToLineMeasureData_NX_WithReturnCode;
    const res: PointToLineMeasureData_NX = result.pointToLineResult;
    const point3d: Vertices3Df = new Vertices3Df(res.foot.pnt_3d.x, res.foot.pnt_3d.y, res.foot.pnt_3d.z);
    const curve = await this.getCurve(res, point3d);

    return this.finalizeMeasurement(result, id, curve, res.distance, res.pntset_ref[0].pnt_3d.z);
  }

  private async handleIV7Mode(item, id) {
    const curve = await this.distortionReproductionCurveAcquisitionAsync(!this.isLineReferenceMeasure(), item.pointToLineResult.foot, null);
    return this.finalizeMeasurement(item, id, curve, item.pointToLineResult.distance, item.pointToLineResult.pntset_meas.pnt_3d.z);
  }

  private async handle3DMode(item, id) {
    const result: PointToLineMeasureData_WithReturnCode = item as PointToLineMeasureData_WithReturnCode;
    const res: PointToLineMeasureData = result.pointToLineResult;
    const point3d: Vertices3Df = new Vertices3Df(res.foot.pnt_3d.x, res.foot.pnt_3d.y, res.foot.pnt_3d.z);
    const curve = await this.getCurve(res, point3d);

    return this.finalizeMeasurement(result, id, curve, res.distance, res.pntset_ref[0].pnt_3d.z);
  }

  private async getCurve(res, point3d) {
    return await this.distortionReproductionCurveAcquisitionAsync(
      !this.isLineReferenceMeasure(),
      [
        {
          x: res.foot.msr_pnt.x,
          y: res.foot.msr_pnt.y
        },
        {
          x: res.foot.res_pnt.x,
          y: res.foot.res_pnt.y
        }
      ],
      point3d
    );
  }

  private finalizeMeasurement(item, id, curve, distance, zValue) {
    this.retentionResult.push(new PlotResultGroup(id, MEASUREMENT_MODE.POINT_TO_LINE, distance, zValue, null, null, this.plotPointList));

    const isCurveProblem = curve.PolylineDataItems
      ? curve.PolylineDataItems[0].returnCode !== 0 || curve.PolylineDataItems[1].returnCode !== 0
      : curve[0].returnCode !== 0 || curve[1].returnCode !== 0;

    this.isApiError = this.isApiError || isCurveProblem;

    if (curve.requirePushMeasurementResult) {
      this.pushMeasurementResult();
    }

    return item.returnCode === 0;
  }

  private async handleDistortionReproductionCurveAsync(
    res: PointToLineMeasureData,
    id: string,
    isCurveError: boolean,
    pointSetToRetention: PointSet
  ) {
    const point3d: Vertices3Df = new Vertices3Df(res.foot.pnt_3d.x, res.foot.pnt_3d.y, res.foot.pnt_3d.z);
    const curve: LineCoordinateResult = await this.distortionReproductionCurveAcquisitionAsync(
      !this.isLineReferenceMeasure(),
      [
        {
          x: res.foot.msr_pnt.x,
          y: res.foot.msr_pnt.y
        },
        { x: res.foot.res_pnt.x, y: res.foot.res_pnt.y }
      ],
      point3d
    );
    this.retentionResult.push(
      new PlotResultGroup(id, MEASUREMENT_MODE.POINT_TO_LINE, res.distance, pointSetToRetention.pnt_3d.z, null, null, this.plotPointList)
    );
    isCurveError = isCurveError ? isCurveError : curve.PolylineDataItems[0].returnCode !== 0 || curve.PolylineDataItems[1].returnCode !== 0;
    this.isApiError = this.isApiError ? true : isCurveError;
    if (curve.requirePushMeasurementResult) {
      this.pushMeasurementResult();
    }
    return isCurveError;
  }

  // Determine whether there is a reference line for line-based measurement
  isLineReferenceMeasure() {
    return this.measurementResult.some(
      (res) => res && res.id && (res.id.match(this.pointToLine[0]) || res.id.match(this.pointToLine[1]) || res.id.match(this.pointToLine[2]))
    );
  }

  // Determine whether a line for reference measurement exists
  isSurfaceReferenceMeasure() {
    return this.measurementResult.some(
      (res) => res && res.id && (res.id.match(this.depth[0]) || res.id.match(this.depth[1]) || res.id.match(this.depth[2]))
    );
  }

  updateObjectInfoOnCanvas(distance: number, objectDistance: number, id: string, area: number, latestDistance: number) {
    // Assign results to the object for saving (when the dialog is displayed again)
    // 2D Canvas
    this.dummyCanvas._objects.forEach((obj) => {
      const data = obj.get("data");
      if (data && data.id === id) {
        obj.set("data", {
          ...data,
          id: id,
          result: { length: distance, objectDistance, area, latestDistance }
        });
        const updatedData = obj.get("data");

        if (this.pointToLine.some((_) => _ === data.mode)) {
          obj.set("data", { ...updatedData, standart: this.referenceLine });
        } else if (this.depth.some((_) => _ === data.mode)) {
          obj.set("data", { ...updatedData, standart: this.referenceSurface });
        }
      }
    });
    this.leftCanvas._objects.forEach((obj) => {
      const data = obj.get("data");
      if (data && data.id === id) {
        obj.set("data", {
          id: id,
          result: { lenght: distance, objectDistance, area, latestDistance }
        });
        const updatedData = obj.get("data");
        if (this.pointToLine.some((_) => _ === data.mode)) {
          obj.set("data", { ...updatedData, standart: this.referenceLine });
        } else if (this.depth.some((_) => _ === data.mode)) {
          obj.set("data", { ...updatedData, standart: this.referenceSurface });
        }
      }
    });

    // 3DCanvas
    if (this.measurementService.is3DMeasurement && this.isReady3d) {
      this.scene.children.forEach((obj) => {
        if (obj.userData && obj.userData.id === id) {
          obj.userData.id = id;
          obj.userData.result.length = distance;
          obj.userData.result.objectDistance = objectDistance;
          obj.userData.result.area = area;
          obj.userData.result.latestDistance = latestDistance;
          if (this.pointToLine.some((_) => _ === obj.userData.mode)) {
            obj.userData.standard = this.referenceLine;
          } else if (this.depth.some((_) => _ === obj.userData.mode)) {
            obj.userData.standard = this.referenceSurface;
          }
        }
      });
    }
    this.collectRenderAll();
    this.emitModifiedData();
  }

  // Revert to the state at the time of the previous marking
  rollBackMeasurement(err: any, message: string) {
    if (this.isLoadMeasurementData) {
      throw new Error(err);
    }
    this.dummyCanvas._objects = this.dummyCanvas._objects.filter((_) => _.type === "image").concat(this.tmpDummyCanvasObj);
    this.rightCanvas._objects = this.rightCanvas._objects.filter((_) => _.type === "image").concat(this.tmpRightCanvasObj);
    this.leftCanvas._objects = this.leftCanvas._objects.filter((_) => _.type === "image").concat(this.tmpLeftCanvasObj);
    this.plotPointList = JSON.parse(JSON.stringify(this.tmpPlotPointList));
    this.measurementResult = JSON.parse(JSON.stringify(this.tmpResult));
    this.referenceLine = JSON.parse(JSON.stringify(this.tmpReferenceLine));
    this.referenceSurface = JSON.parse(JSON.stringify(this.tmpReferenceSurface));
    this.retentionResult = JSON.parse(JSON.stringify(this.tmpRetentionResult));

    this.collectRenderAll();
    this.emitModifiedData();
    this.resizeAfterLoading();
    this.snackBar.open(message);
    this.oscSpinnerService.hide(true);
    this.isApiError = true;
    console.log(err);
  }

  // Generate ID
  getResultId(): string {
    if (this.selectData.length > 0 && this.measureCount >= 0) {
      return this.selectData[this.measureCount].id;
    } else if (this.selectMode || !this.isDuringMeasure) {
      const num = this.measurementResult.length + 1;
      return num + " : " + this.selectMode;
    } else {
      return null;
    }
  }

  public removeObject(result: MeasurementResult): void {
    const hasPlotPoints = this.plotPointList.length > 0;
    const hasMeasurementResults = this.measurementResult.length > 0;
    const is3DReady = this.measurementService.is3DMeasurement && this.isReady3d;

    if (this.selectMode === MEASUREMENT_MODE.LINES && this.plotPointList.length !== 1) {
      this.deleteObject(result);
    } else if (hasPlotPoints) {
      this.deleteUnmeasuredData();
    } else if (hasMeasurementResults) {
      this.deleteObject(result);
    } else {
      this.clearCanvasObjects();
      if (is3DReady) {
        this.scene.children = this.scene.children.filter((obj) => obj.name === "3dModel");
      }
    }
    this.resetState();
    this.emitModifiedData();
  }

  private clearCanvasObjects(): void {
    const filterImagesOnly = (obj: any) => obj.type.toLowerCase() === "image";

    this.dummyCanvas._objects = this.dummyCanvas._objects.filter(filterImagesOnly);
    this.rightCanvas._objects = this.rightCanvas._objects.filter(filterImagesOnly);
    this.leftCanvas._objects = this.leftCanvas._objects.filter(filterImagesOnly);
  }

  private resetState(): void {
    this.selectData = [];
    this.movePointList = [];
    this.retentionResult = [];
    this.plotPointList = [];
    this.leftCanvas.discardActiveObject();
    this.collectRenderAll();
  }

  deleteObject(result: MeasurementResult) {
    this.measurementNumData = [];
    const selectId = result.id;

    // Delete the specified result from the 3DCanvas
    if (this.measurementService.is3DMeasurement && this.isReady3d) {
      this.scene.children = this.scene.children.filter((item) => {
        return item.name === "3dModel" || (item.userData.id !== selectId && !item.userData.isNum) || item.userData.isStandard;
      });
    }

    // Delete the specified result from the 2DCanvas
    this.dummyCanvas._objects = this.dummyCanvas._objects.filter((obj) => {
      const data = obj.get("data");
      if (!obj.isType("image") && data.isNum && data.id !== selectId) {
        const coordinate = new Vertices2Df(obj.left, obj.top);
        this.measurementNumData.push([coordinate, data]);
      }
      return obj.isType("image") || (data.id !== selectId && !data.isNum && !data.isStandard) || data.isStandard;
    });

    this.rightCanvas._objects = this.rightCanvas._objects.filter((obj) => {
      const data = obj.get("data");
      return obj.isType("image") || (data.id !== selectId && !data.isNum && !data.isStandard) || data.isStandard;
    });

    this.leftCanvas._objects = this.leftCanvas._objects.filter((obj) => {
      const data = obj.get("data");
      return obj.isType("image") || (data.id !== selectId && !data.isNum && !data.isStandard) || data.isStandard;
    });

    // Delete the specified result from the measurement results
    this.measurementResult = this.measurementResult.filter((item) => item.id && item.id !== selectId);

    // Delete reference lines (or surfaces) if they are not needed
    const separationId = selectId.split(":").map((_: string) => _.trim());
    if (
      this.pointToLine.some((_) => _ === separationId[1]) &&
      !this.measurementResult.some((item) => this.pointToLine.some((_) => _ === item.mode))
    ) {
      this.dummyCanvas._objects = this.dummyCanvas._objects.filter(
        (obj) => obj.isType("image") || !this.pointToLine.some((_) => _ === obj.get("data").mode)
      );
      this.rightCanvas._objects = this.rightCanvas._objects.filter(
        (obj) => obj.isType("image") || !this.pointToLine.some((_) => _ === obj.get("data").mode)
      );
      this.leftCanvas._objects = this.leftCanvas._objects.filter(
        (obj) => obj.isType("image") || !this.pointToLine.some((_) => _ === obj.get("data").mode)
      );
      if (this.measurementService.is3DMeasurement && this.isReady3d) {
        this.scene.children = this.scene.children.filter(
          (item) => item.name === "3dModel" || !this.pointToLine.some((_) => _ === item.userData.mode)
        );
      }
      this.referenceLine = [];
    } else if (this.depth.some((_) => _ === separationId[1]) && !this.measurementResult.some((item) => this.depth.some((_) => _ === item.mode))) {
      this.dummyCanvas._objects = this.dummyCanvas._objects.filter(
        (obj) => obj.isType("image") || !this.depth.some((_) => _ === obj.get("data").mode)
      );
      this.rightCanvas._objects = this.rightCanvas._objects.filter(
        (obj) => obj.isType("image") || !this.depth.some((_) => _ === obj.get("data").mode)
      );
      this.leftCanvas._objects = this.leftCanvas._objects.filter((obj) => obj.isType("image") || !this.depth.some((_) => _ === obj.get("data").mode));
      if (this.measurementService.is3DMeasurement && this.isReady3d) {
        this.scene.children = this.scene.children.filter((item) => item.name === "3dModel" || !this.depth.some((_) => _ === item.userData.mode));
      }
      this.referenceSurface = [];
    }

    this.renameCanvasNum(selectId);
  }

  // Delete unmeasured lines and points within the canvas
  deleteUnmeasuredData() {
    this.dummyCanvas._objects = this.dummyCanvas._objects.filter(
      (obj) => obj.isType("image") || this.measurementResult.some((item) => obj.get("data").id === item.id)
    );
    this.rightCanvas._objects = this.rightCanvas._objects.filter(
      (obj) => obj.isType("image") || this.measurementResult.some((item) => obj.get("data").id === item.id)
    );
    this.leftCanvas._objects = this.leftCanvas._objects.filter(
      (obj) => obj.isType("image") || this.measurementResult.some((item) => obj.get("data").id === item.id)
    );
    if (this.measurementService.is3DMeasurement && this.isReady3d) {
      this.scene.children = this.scene.children.filter(
        (obj) => obj.name === "3dModel" || this.measurementResult.some((item) => obj.userData.id === item.id)
      );
    }
    this.emitModifiedData();
  }

  renameMeasurementResult() {
    this.measurementResult.forEach((item, i) => {
      const id = (i + 1).toString() + " : " + item.mode;

      // Rename the ID of the results within the 2D Canvas
      this.dummyCanvas._objects.forEach((obj) => {
        const data = obj.get("data");
        if (!obj.isType("image") && item.id === data.id && !data.isNum) {
          obj.set("data", { ...data, id });
        }
      });
      this.leftCanvas._objects.forEach((obj) => {
        const data = obj.get("data");
        if (!obj.isType("image") && item.id === data.id && !data.isNum) {
          obj.set("data", { ...data, id });
        }
      });
      this.rightCanvas._objects.forEach((obj) => {
        const data = obj.get("data");
        if (!obj.isType("image") && item.id === data.id && !data.isNum) {
          obj.set("data", { ...data, id });
        }
      });

      // Rename the ID of the results within the 3D Canvas
      if (this.measurementService.is3DMeasurement && this.isReady3d) {
        this.scene.children.forEach((obj) => {
          if (obj.name !== "3dModel" && obj.type !== "Sprite" && item.id === obj.userData.id && !obj.userData.isNum) {
            obj.userData.id = id;
          }
        });
      }

      item.id = id;
    });

    if (this.measurementResult.length > 0) {
      this.selectResult = this.measurementResult[this.measurementResult.length - 1];
    }

    this.emitModifiedData();
  }

  renameCanvasNum(selectId: string) {
    this.measurementResult = [];
    this.dummyCanvas._objects.forEach((obj) => {
      const data = obj.get("data");
      if (!obj.isType("image") && (this.measurementResult.length === 0 || !this.measurementResult.some((_) => _.id === data.id))) {
        const res = MeasurementResultFactory.createMeasurementResult(
          new PlotResultGroup(
            data.id,
            data.mode,
            data.result.length,
            data.result.objectDistance,
            data.result.area,
            data.result.latestDistance,
            data.plotList
          )
        );
        if (res.id !== selectId) {
          this.measurementResult.push(res);
        }
      }
    });

    // Reassign measurement numbers within the canvas results
    let i = 1;
    this.measurementNumData.forEach((item, index) => {
      this.measurementNumSet(null, item, i);
      if (index % 2 !== 0) {
        i++;
      }
      if (this.measurementNumData.length === index + 1) {
        this.renameMeasurementResult();
      }
    });
  }

  // Measurement mode change event
  changeMode(event: string) {
    if (!this.selectMode) {
      this.getReferenceLineAndSurface();
    }

    if (
      (this.plotPointList.length > 0 && this.selectMode !== MEASUREMENT_MODE.LINES) ||
      (this.plotPointList.length === 1 && this.selectMode === MEASUREMENT_MODE.LINES)
    ) {
      this.openPointDeletionConfirmationDialog(this.selectMode, event);
    } else {
      this.changeSelectMode(event);
      this.plotPointList = [];
      this.retentionResult = [];
      this.selectData = [];
      this.movePointList = [];
      this.movePointResult = [];
      this.isDuringMeasure = false;
      this.selectMode = event;
    }
  }

  // Obtain reference lines (or planes) necessary for line (or plane) based measurement from object information
  getReferenceLineAndSurface() {
    if (!this.selectMode) {
      // Retain the stored reference lines (or surfaces)
      this.dummyCanvas._objects.forEach((item) => {
        const data = item.get("data");
        if (item.type !== "image" && this.pointToLine.some((_) => _ === data.mode)) {
          if (data.standard.length >= 3) {
            this.referenceLine = data.standard;
            return;
          }
        } else if (item.type !== "image" && this.depth.some((_) => _ === data.mode)) {
          if (data.standard.length >= 4) {
            this.referenceSurface = data.standard;
            return;
          }
        }
      });
    }
  }

  // Event triggered when the left canvas is clicked
  async onAddPoint(left: number, top: number) {
    if (this.leftCanvas.getActiveObject() || this.selectData.length > 0) {
      return;
    }
    this.isApiError = false;
    if (this.selectMode && this.isPlotArea(left, top) && this.isPlotNumber() && this.isEditable && !this.isPointAddingToCanvas) {
      try {
        this.isPointAddingToCanvas = true;
        this.isLoading = true;
        // Snapshot/reset only for the first point of a measurement; intermediate clicks must preserve mid-measurement state.
        if (this.plotPointList.length === 0) {
          this.isLMeasurement = false;
          this.isRMeasurement = false;
          this.isLMeasurement2 = false;
          this.isRMeasurement2 = false;
          this.tmpDummyCanvasObj = this.dummyCanvas._objects.filter((_) => _.type !== "image");
          this.tmpLeftCanvasObj = this.leftCanvas._objects.filter((_) => _.type !== "image");
          this.tmpRightCanvasObj = this.rightCanvas._objects.filter((_) => _.type !== "image");
          this.tmpPlotPointList = JSON.parse(JSON.stringify(this.plotPointList));
          this.tmpResult = JSON.parse(JSON.stringify(this.measurementResult));
          this.tmpReferenceSurface = JSON.parse(JSON.stringify(this.referenceSurface));
          this.tmpReferenceLine = JSON.parse(JSON.stringify(this.referenceLine));
          this.tmpRetentionResult = JSON.parse(JSON.stringify(this.retentionResult));
        }
        this.oscSpinnerService.show(true);
        this.oscSpinnerService.displayMessage(this.translate.instant("imageRemeasurementEdit.loadingMessageMeasuringProcess"));
        await this.addPoint(left, top);
      } finally {
        this.isPointAddingToCanvas = false;
      }
    }
  }

  /** Place objects on the canvas */
  public async addPoint(left: number, top: number): Promise<void> {
    if (this.isCanvasLoading) {
      return;
    }

    // Calculate so that the center of the annotation comes to the clicked location
    const data = new Vertices2Df(left / this.imageMagnification / this.scale, top / this.imageMagnification / this.scale);

    // In Lines mode, necessary to update rather than add results each time a point is marked
    if (this.selectMode === MEASUREMENT_MODE.LINES && this.plotPointList.length > 1 && this.retentionResult[0]) {
      this.measurementResult = this.measurementResult.filter((item) => item.id !== this.retentionResult[0].id);
    }

    const pointSet = await this.measureDotCoordinateDataSetAndDrawingAsync(data, null);
    const isMatch = pointSet.pointSetResult.isMatch && pointSet.returnCode === 0;

    if (
      (this.selectMode === MEASUREMENT_MODE.DISTANCE && this.plotPointList.length === 1) ||
      (this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE && this.plotPointList.length === 1 && !this.isLineReferenceMeasure()) ||
      (this.selectMode === MEASUREMENT_MODE.DEPTH && this.plotPointList.length === 1 && !this.isSurfaceReferenceMeasure()) ||
      (this.selectMode === MEASUREMENT_MODE.LINES && this.plotPointList.length === 1) ||
      (this.selectMode === MEASUREMENT_MODE.AREA && this.plotPointList.length === 1) ||
      (this.selectMode === MEASUREMENT_MODE.MISSING_CORNER && this.plotPointList.length === 1) ||
      (this.selectMode === MEASUREMENT_MODE.MISSING_CORNER && this.plotPointList.length === 3)
    ) {
      this.oscSpinnerService.hide(true);
      this.resizeAfterLoading();
    }
    if (isMatch) {
      await this.addMeasurementResult();
    } else {
      this.oscSpinnerService.hide(true);
      this.resizeAfterLoading();
    }
  }

  get3dLineColor(foot: any): string {
    if (
      foot ||
      this.selectMode === MEASUREMENT_MODE.DISTANCE ||
      this.selectMode === MEASUREMENT_MODE.LINES ||
      this.selectMode === MEASUREMENT_MODE.AREA
    ) {
      return this.measurementColor;
    } else {
      return this.referenceLineColor;
    }
  }

  /**
   * @param leftPoints2d
   * @param rightPoints2d
   * @param startAndEndPoints3d
   * @param lineType Type of line (0: line segment, 1: extend the line to the edge of the image)
   * @param isSurface Whether it is a reference plane for surface-based measurement
   * @returns An array of PolylineData_IV7_WithReturnCode, indicates whether it needs to be added to the list of measurement results (result of createCurve). If addition is needed, call pushMeasurementResult() at the source.
   */
  private async getLineCoordinatesAsync(
    leftPoints2d: Vertices2Df[],
    rightPoints2d: Vertices2Df[],
    startAndEndPoints3d: Vertices3Df[],
    lineType: number,
    isSurface: boolean
  ): Promise<LineCoordinateResult> {
    return await this.measurementService
      .getDistortionReproductionCurveAsync(this.inspectionImage.inspectionId, this.inspectionImage.id, leftPoints2d, rightPoints2d, lineType)
      .then((item: Array<PolylineData_IV7_WithReturnCode>) => {
        const requirePushMeasurementResult1: boolean = this.createCurve(item[0].polylineResult, true, isSurface, startAndEndPoints3d, true);
        const requirePushMeasurementResult2: boolean = this.createCurve(item[1].polylineResult, false, isSurface, startAndEndPoints3d, true);
        return {
          PolylineDataItems: item,
          requirePushMeasurementResult: requirePushMeasurementResult1 || requirePushMeasurementResult2
        };
      });
  }

  // Reflect the measurement results on the screen
  private pushMeasurementResult(): void {
    if (this.isApiError) {
      return;
    }

    if (
      (this.selectMode === MEASUREMENT_MODE.DISTANCE && this.plotPointList.length === 2) ||
      (this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE && this.plotPointList.length === 3) ||
      (this.isLineReferenceMeasure() && this.plotPointList.length === 1 && this.selectMode !== MEASUREMENT_MODE.MISSING_CORNER)
    ) {
      if (
        !this.measurementResult ||
        this.measurementResult.length === 0 ||
        !this.measurementResult.some((obj) => obj?.id === this.retentionResult[0].id)
      ) {
        this.updateObjectInfoOnCanvas(this.retentionResult[0].length, this.retentionResult[0].objectDistance, this.retentionResult[0].id, null, null);
        this.measurementResult.push(MeasurementResultFactory.createMeasurementResult(this.retentionResult[0]));
        this.plotPointList = [];
        this.retentionResult = [];
      }
    } else if (this.selectMode === MEASUREMENT_MODE.LINES) {
      // Add if there is no duplicate
      if (!this.measurementResult.some((value) => value?.id === this.retentionResult[0].id)) {
        const result: PlotResultGroup = new PlotResultGroup(
          this.retentionResult[0].id,
          this.selectMode,
          this.retentionResult[this.retentionResult.length - 1].length,
          this.retentionResult[this.retentionResult.length - 1].objectDistance,
          this.retentionResult[this.retentionResult.length - 1].area,
          this.retentionResult[this.retentionResult.length - 1].latestDistance,
          this.plotPointList
        );
        this.updateObjectInfoOnCanvas(result.length, result.objectDistance, this.retentionResult[0].id, result.area, result.latestDistance);
        this.measurementResult.push(MeasurementResultFactory.createMeasurementResult(result));
        if (this.plotPointList.length === this.maxPoints) {
          this.openConfirmMeasureCompletionDialog();
        }
      }
    } else if (
      this.plotPointList.length >= 4 &&
      this.isCrossingLines(
        this.plotPointList[this.plotPointList.length - 2].msr_pnt.x,
        this.plotPointList[this.plotPointList.length - 2].msr_pnt.y,
        this.plotPointList[this.plotPointList.length - 1].msr_pnt.x,
        this.plotPointList[this.plotPointList.length - 1].msr_pnt.y
      )
    ) {
      this.updateObjectInfoOnCanvas(
        this.retentionResult[this.retentionResult.length - 1].length,
        this.retentionResult[this.retentionResult.length - 1].objectDistance,
        this.retentionResult[this.retentionResult.length - 1].id,
        this.retentionResult[this.retentionResult.length - 1].area,
        this.retentionResult[this.retentionResult.length - 1].latestDistance
      );
      this.measurementResult.push(MeasurementResultFactory.createMeasurementResult(this.retentionResult[this.retentionResult.length - 1]));
      if (this.plotPointList.length === this.maxPoints) {
        this.openConfirmMeasureCompletionDialog();
      }
      this.plotPointList = [];
      this.retentionResult = [];
    }
    this.selectResult = this.measurementResult[this.measurementResult.length - 1];
    this.oscSpinnerService.hide(true);
    this.resizeAfterLoading();
  }

  measurementNumSet(item: PointSet | PointSet_IV7, measureData: any, num: number): void {
    if (this.isApiError) {
      return;
    }

    const point3d = this.measurementService.is3DMeasurement ? (item ? item.pnt_3d : null) : null;
    let leftObj, rightObj;
    let dLeftObj, dRightObj;
    if (!measureData) {
      const text =
        this.selectData.length > 0 && this.measureCount >= 0
          ? Number(this.selectData[this.measureCount].id.slice(0, 1)).toString()
          : (this.measurementResult.length + 1).toString();

      if (
        (this.selectMode === MEASUREMENT_MODE.DISTANCE && this.plotPointList.length === 0) ||
        (this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE && this.isLineReferenceMeasure()) ||
        (this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE && this.plotPointList.length === 2) ||
        (this.selectMode === MEASUREMENT_MODE.DEPTH && this.isSurfaceReferenceMeasure()) ||
        (this.selectMode === MEASUREMENT_MODE.DEPTH && this.plotPointList.length === 3) ||
        (this.selectMode === MEASUREMENT_MODE.LINES && this.plotPointList.length === 0) ||
        (this.selectMode === MEASUREMENT_MODE.AREA && this.plotPointList.length === 0) ||
        (this.selectMode === MEASUREMENT_MODE.MISSING_CORNER && this.plotPointList.length === 0) ||
        (this.selectMode === MEASUREMENT_MODE.MISSING_CORNER && this.plotPointList.length === 3)
      ) {
        leftObj = this.generateText(
          (item.msr_pnt.x + 14) * this.scale,
          (item.msr_pnt.y - 20) * this.scale,
          point3d,
          text,
          this.measurementColor,
          20 * this.scale,
          true
        );
        rightObj = this.generateText(
          (item.res_pnt.x + 14) * this.scale - this.halfMainImgW,
          (item.res_pnt.y - 20) * this.scale,
          point3d,
          text,
          this.measurementColor,
          20 * this.scale,
          true
        );
        dLeftObj = this.generateText(item.msr_pnt.x + 14, item.msr_pnt.y - 20, point3d, text, this.measurementColor, 20, true);
        dRightObj = this.generateText(item.res_pnt.x + 14, item.res_pnt.y - 20, point3d, text, this.measurementColor, 20, true);

        // Necessary to prevent selected objects from being placed
        leftObj.selectable = false;
        rightObj.selectable = false;

        // Place annotations on the leftCanvas and rightCanvas
        this.leftCanvas.add(leftObj);
        this.rightCanvas.add(rightObj);

        // Place the annotation at the same coordinates as the leftCanvas and rightCanvas
        this.dummyCanvas.add(dLeftObj, dRightObj);
      }
    } else {
      const text = num.toString();
      const data = measureData[1];
      data.id = num.toString() + " : " + data.mode;

      leftObj = this.generateText(
        measureData[0].x * this.scale,
        measureData[0].y * this.scale,
        point3d,
        text,
        this.measurementColor,
        20 * this.scale,
        true,
        data
      );
      rightObj = this.generateText(
        measureData[0].x * this.scale - this.halfMainImgW,
        measureData[0].y * this.scale,
        point3d,
        text,
        this.measurementColor,
        20 * this.scale,
        true,
        data
      );
      dLeftObj = this.generateText(measureData[0].x, measureData[0].y, point3d, text, this.measurementColor, 20, true, data);
      dRightObj = this.generateText(measureData[0].x, measureData[0].y, point3d, text, this.measurementColor, 20, true, data);

      leftObj.selectable = false;
      rightObj.selectable = false;

      // leftCanvasとrightCanvasにアノテーションを配置する
      if (measureData[0].x * this.scale > this.halfMainImgW) {
        this.rightCanvas.add(rightObj);
        this.dummyCanvas.add(dRightObj);
      } else {
        this.leftCanvas.add(leftObj);
        this.dummyCanvas.add(dLeftObj);
      }
    }

    this.leftCanvas.isDrawingMode = false;
    this.collectRenderAll();
    this.emitModifiedData();
  }

  dataset2d(setPoint, resPoint, isFoot: boolean = false, coordinate: Vertices3Df): void {
    if (this.isApiError) {
      return;
    }

    const point3d = this.measurementService.is3DMeasurement ? coordinate : null;
    const leftObj = this.generateText(
      setPoint.x * this.scale,
      setPoint.y * this.scale,
      point3d,
      this.pointText(isFoot),
      this.pointColor(isFoot),
      this.pointSize(isFoot) * this.scale,
      false
    );
    const rightObj = this.generateText(
      resPoint.x * this.scale - this.halfMainImgW,
      resPoint.y * this.scale,
      point3d,
      this.pointText(isFoot),
      this.pointColor(isFoot),
      this.pointSize(isFoot) * this.scale,
      false
    );
    const dLeftObj = this.generateText(
      setPoint.x,
      setPoint.y,
      point3d,
      this.pointText(isFoot),
      this.pointColor(isFoot),
      this.pointSize(isFoot),
      false
    );
    const dRightObj = this.generateText(
      resPoint.x,
      resPoint.y,
      point3d,
      this.pointText(isFoot),
      this.pointColor(isFoot),
      this.pointSize(isFoot),
      false
    );
    if (this.measurementService.is3DMeasurement && this.isReady3d) {
      this.dataset3d(coordinate, "mode", "id", true, this.pointColor(isFoot));
    }

    // 配置済みのオブジェクトを選択出来なくする為に必要

    leftObj.selectable = leftObj.text === "×" || leftObj.text === "◇";
    leftObj.hasControls = false;
    rightObj.selectable = false;

    // Place annotations on the leftCanvas and rightCanvas
    this.leftCanvas.add(leftObj);
    this.rightCanvas.add(rightObj);

    // Place the annotation at the same coordinates as the leftCanvas and rightCanvas
    this.dummyCanvas.add(dLeftObj, dRightObj);

    this.leftCanvas.isDrawingMode = false;
    this.collectRenderAll();
  }

  isStandard(color: string): boolean {
    if (this.selectMode === MEASUREMENT_MODE.DISTANCE) {
      return false;
    } else if (this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE) {
      return !this.referenceLine || this.referenceLine.length === 0 || color === this.referenceLineColor;
    } else if (this.selectMode === MEASUREMENT_MODE.DEPTH) {
      return !this.referenceSurface || this.referenceSurface.length === 0 || color === this.referenceLineColor;
    }
  }

  // Change the cursor if measurement is possible
  moveCanvas(event: any) {
    const circle = event.target.getBoundingClientRect();
    const left = event.clientX - circle.left;
    const top = event.clientY - circle.top;

    if (this.isEditable && this.isSelectMode) {
      this.leftCanvas.hoverCursor = "pointer";
    } else if (this.isPlotArea(left, top) && this.isPlotNumber() && this.isEditable && this.selectData.length <= 0) {
      this.leftCanvas.hoverCursor = "crosshair";
    } else {
      this.leftCanvas.hoverCursor = "default";
    }
  }

  // Determine whether it is a measurable area
  isPlotArea(left: number, top: number): boolean {
    const leftPlotArea = this.measurementInfoData?.areaInfo?.roi_LD;
    if (!leftPlotArea) {
      return false;
    }
    const minXL = leftPlotArea.origin.x * this.imageMagnification * this.scale;
    const minYL = leftPlotArea.origin.y * this.imageMagnification * this.scale;
    const maxXL = minXL + leftPlotArea.size.width * this.imageMagnification * this.scale;
    const maxYL = minYL + leftPlotArea.size.height * this.imageMagnification * this.scale;

    return left >= minXL && left <= maxXL && top >= minYL && top <= maxYL;
  }

  // Determine whether marking can be done in the current
  isPlotNumber(): boolean {
    return this.maxPoints > this.plotPointList.length;
  }

  collectRenderAll() {
    this.leftCanvas.renderAll();
    this.rightCanvas.renderAll();
    this.dummyCanvas.renderAll();
  }

  openPointDeletionConfirmationDialog(oldMode: string, newMode: string): void {
    const dialogRef = this.dialog.open(CommonConfirmDialogComponent, {
      width: "500px",
      height: "244px",
      data: {
        firstMessage: this.translate.instant("imageRemeasurementEdit.deleteConfirm"),
        secondMessage: "",
        dialogType: "confirmation",
        trueBtnText: "OK"
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.selectData = [];
        this.movePointList = [];
        this.movePointResult = [];
        this.isDuringMeasure = false;
        this.retentionResult = [];
        this.plotPointList = [];
        this.selectMode = newMode;
        this.deleteUnmeasuredData();
        this.collectRenderAll();
        this.changeSelectMode(newMode);
      } else {
        this.selectMode = oldMode;
      }
    });
  }

  openConfirmMeasureCompletionDialog() {
    const dialogRef = this.dialog.open(CommonConfirmDialogComponent, {
      width: "500px",
      height: "280px",
      data: {
        firstMessage: this.translate.instant("imageRemeasurementEdit.maxPointConfirmFirst"),
        secondMessage: this.translate.instant("imageRemeasurementEdit.maxPointConfirmSecond"),
        dialogType: "information",
        trueBtnText: "OK"
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.plotPointList = [];
      this.retentionResult = [];
    });
  }

  changeSelectMode(event: string) {
    const modePointMapping: { [key: string]: number } = {
      [MEASUREMENT_MODE.DISTANCE]: 2,
      [MEASUREMENT_MODE.POINT_TO_LINE]: 3,
      [MEASUREMENT_MODE.DEPTH]: 4,
      [MEASUREMENT_MODE.LINES]: 20,
      [MEASUREMENT_MODE.AREA]: 20,
      [MEASUREMENT_MODE.MISSING_CORNER]: 4
    };

    this.maxPoints = modePointMapping[event] || 0;
  }

  getDspMode(mode): string {
    if (!mode) {
      return;
    }
    return this.translate.instant(`imageRemeasurementEdit.${mode}`);
  }

  isAddPoints(): boolean {
    return this.dummyCanvas && this.dummyCanvas._objects && this.dummyCanvas._objects.length > 1;
  }

  /** Returns whether it is necessary to add to the list of measurement results. If addition is necessary, call pushMeasurementResult() at the source. */
  private createCurve(item: PolylineData, isLeft: boolean, isSurface: boolean, points3d: Vertices3Df[], isAsync: boolean): boolean {
    let requirePushMeasurementResult: boolean = false;

    if (this.isApiError) {
      return requirePushMeasurementResult;
    }

    let color: string;
    // 線色
    if (
      this.selectMode === MEASUREMENT_MODE.DISTANCE ||
      (this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE && this.plotPointList.length === 3) ||
      (this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE && this.isLineReferenceMeasure()) ||
      this.selectMode === MEASUREMENT_MODE.LINES ||
      this.selectMode === MEASUREMENT_MODE.AREA ||
      (this.isLoadMeasurementData && this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE && this.plotPointList.length === 4)
    ) {
      color = this.measurementColor;
    } else {
      color = this.referenceLineColor;
    }

    // 線がマッチした点の座標で作成されたものかどうか
    let isMatchLine = true;
    if (isAsync) {
      if (!isSurface) {
        isMatchLine =
          this.plotPointList[0] &&
          this.plotPointList[this.plotPointList.length - 1] &&
          this.plotPointList[0].isMatch &&
          this.plotPointList[this.plotPointList.length - 1].isMatch;
      } else {
        isMatchLine =
          this.plotPointList[this.plotPointList.length - 1] &&
          this.plotPointList[this.plotPointList.length - 2] &&
          this.plotPointList[this.plotPointList.length - 1].isMatch &&
          this.plotPointList[this.plotPointList.length - 2].isMatch;
      }
    }

    if (isMatchLine === false && !isLeft) {
      let start2d: Vertices2Df, end2d: Vertices2Df, dStart2d: Vertices2Df, dEnd2d: Vertices2Df, start3d: Vertices3Df, end3d: Vertices3Df;
      const len = this.plotPointList.length;
      if (isSurface) {
        start2d = new Vertices2Df(this.plotPointList[0].res_pnt.x * this.scale - this.halfMainImgW, this.plotPointList[0].res_pnt.y * this.scale);
        end2d = new Vertices2Df(
          this.plotPointList[len - 1].res_pnt.x * this.scale - this.halfMainImgW,
          this.plotPointList[len - 1].res_pnt.y * this.scale
        );
        dStart2d = new Vertices2Df(this.plotPointList[0].res_pnt.x, this.plotPointList[0].res_pnt.y);
        dEnd2d = new Vertices2Df(this.plotPointList[len - 1].res_pnt.x, this.plotPointList[len - 1].res_pnt.y);
        start3d = this.measurementService.is3DMeasurement ? new Vertices3Df(points3d[0].x, points3d[0].y, points3d[0].z) : null;
        end3d = this.measurementService.is3DMeasurement ? new Vertices3Df(points3d[1].x, points3d[1].y, points3d[1].z) : null;
      } else {
        start2d = new Vertices2Df(
          this.plotPointList[len - 1].res_pnt.x * this.scale - this.halfMainImgW,
          this.plotPointList[len - 1].res_pnt.y * this.scale
        );
        end2d = new Vertices2Df(
          this.plotPointList[len - 2].res_pnt.x * this.scale - this.halfMainImgW,
          this.plotPointList[len - 2].res_pnt.y * this.scale
        );
        dStart2d = new Vertices2Df(this.plotPointList[len - 1].res_pnt.x, this.plotPointList[len - 1].res_pnt.y);
        dEnd2d = new Vertices2Df(this.plotPointList[len - 2].res_pnt.x, this.plotPointList[len - 2].res_pnt.y);
        start3d = this.measurementService.is3DMeasurement ? new Vertices3Df(points3d[0].x, points3d[0].y, points3d[0].z) : null;
        end3d = this.measurementService.is3DMeasurement ? new Vertices3Df(points3d[1].x, points3d[1].y, points3d[1].z) : null;
      }

      const rightObj = this.generateLine(start2d, end2d, start3d, end3d, 1 * this.scale, color);
      const dummyObj = this.generateLine(dStart2d, dEnd2d, start3d, end3d, 1, color);
      rightObj.selectable = false;

      this.rightCanvas.add(rightObj);
      this.dummyCanvas.add(dummyObj);

      if (!isSurface) {
        this.isRMeasurement = true;
      } else {
        this.isRMeasurement2 = true;
      }
    } else if (isLeft) {
      let tmpData: Vertices2Df;
      item?.pnt_poly?.forEach((val, i) => {
        if (i > 0) {
          const start2d = new Vertices2Df(tmpData.x * this.scale, tmpData.y * this.scale);
          const end2d = new Vertices2Df(val.x * this.scale, val.y * this.scale);
          const dStart2d = new Vertices2Df(tmpData.x, tmpData.y);
          const dEnd2d = new Vertices2Df(val.x, val.y);
          const start3d = this.measurementService.is3DMeasurement ? new Vertices3Df(points3d[0].x, points3d[0].y, points3d[0].z) : null;
          const end3d = this.measurementService.is3DMeasurement ? new Vertices3Df(points3d[1].x, points3d[1].y, points3d[1].z) : null;
          const leftObj = this.generateLine(start2d, end2d, start3d, end3d, 1 * this.scale, color);
          const dummyObj = this.generateLine(dStart2d, dEnd2d, start3d, end3d, 1, color);

          // 配置済みのオブジェクトを選択出来なくする為に必要
          leftObj.selectable = false;

          this.leftCanvas.add(leftObj);
          this.dummyCanvas.add(dummyObj);
        }
        tmpData = val;
      });
      if (!isSurface) {
        this.isLMeasurement = true;
      } else {
        this.isLMeasurement2 = true;
      }
    } else {
      // 右Canvasと保存用Canvasの右側
      let tmpData: Vertices2Df;
      item?.pnt_poly?.forEach((val, i) => {
        if (i > 0) {
          const start2d = new Vertices2Df(tmpData.x * this.scale - this.halfMainImgW, tmpData.y * this.scale);
          const end2d = new Vertices2Df(val.x * this.scale - this.halfMainImgW, val.y * this.scale);
          const dStart2d = new Vertices2Df(tmpData.x, tmpData.y);
          const dEnd2d = new Vertices2Df(val.x, val.y);
          const start3d = this.measurementService.is3DMeasurement ? new Vertices3Df(points3d[0].x, points3d[0].y, points3d[0].z) : null;
          const end3d = this.measurementService.is3DMeasurement ? new Vertices3Df(points3d[1].x, points3d[1].y, points3d[1].z) : null;
          const rightObj = this.generateLine(start2d, end2d, start3d, end3d, 1 * this.scale, color);
          const dummyObj = this.generateLine(dStart2d, dEnd2d, start3d, end3d, 1, color);

          rightObj.selectable = false;

          this.rightCanvas.add(rightObj);
          this.dummyCanvas.add(dummyObj);
        }
        tmpData = val;
      });
      if (!isSurface) {
        this.isRMeasurement = true;
      } else {
        this.isRMeasurement2 = true;
      }
    }

    if (
      !this.isLoadMeasurementData &&
      ((this.selectMode !== MEASUREMENT_MODE.DEPTH && this.isLMeasurement && this.isRMeasurement) ||
        (this.selectMode === MEASUREMENT_MODE.DEPTH && this.plotPointList.length === 2 && this.isLMeasurement && this.isRMeasurement) ||
        (this.selectMode === MEASUREMENT_MODE.DEPTH &&
          this.plotPointList.length === 3 &&
          this.isLMeasurement &&
          this.isRMeasurement &&
          this.isLMeasurement2 &&
          this.isRMeasurement2))
    ) {
      requirePushMeasurementResult = true;
    }

    return requirePushMeasurementResult;
  }

  private generateText(
    left: number,
    top: number,
    point3d: Vertices3Df,
    text: string,
    color: string,
    fontSize: number,
    isNum: boolean = false,
    measureData: any = null
  ): FabricText {
    const obj = new FabricText(text, {
      left: left,
      top: top,
      fill: color,
      fontSize: fontSize,
      originX: "center",
      originY: "center",
      data: measureData
        ? measureData
        : this.getPointData(isNum, this.measurementService.is3DMeasurement ? point3d : null, this.selectMode, this.getResultId(), text === "◇")
    });
    return obj;
  }

  getPolygonData(id: string, points: Vertices2Df[]) {
    return {
      id: id,
      isLine: false,
      unit: this.unitSelected,
      points: points,
      result: {
        length: null,
        objectDistance: null,
        area: null,
        latestDistance: null
      }
    };
  }

  getPointData(isNum: boolean, coordinate: Vertices3Df, mode: string, id: string, isStandard: boolean): any {
    return {
      id: id,
      mode: mode,
      isLine: false,
      standard: [],
      isStandard: isStandard,
      isNum: isNum,
      coordinate: coordinate,
      startEnd: null,
      unit: this.unitSelected,

      result: {
        // Assign the result after measurement
        length: null,
        objectDistance: null,
        area: null,
        latestDistance: null
      }
    };
  }

  generateLine(start2d: Vertices2Df, end2d: Vertices2Df, start3d: Vertices3Df, end3d: Vertices3Df, width: number, color: string): Line {
    const obj = new Line([start2d.x, start2d.y, end2d.x, end2d.y], {
      stroke: color,
      strokeWidth: width,
      data: this.getLineData(start3d, end3d, this.selectMode, this.getResultId(), this.isStandard(color))
    });

    return obj;
  }

  drawMissingCornerResult2D(resultPoints: PointSet[], id: string) {
    // 9 points total, first 4 are input points, next 3 form a triangle for the missing corner and last 2 are different depths for the corner point.
    const missingCornerPoints = resultPoints.slice(4, 7);
    const polygonPoints = missingCornerPoints.map((point) => new Vertices2Df(point.msr_pnt.x * this.scale, point.msr_pnt.y * this.scale));
    const polygon = new Polygon(polygonPoints, {
      fill: this.areaFillColor,
      opacity: 0.7
    });
    polygon.set("data", this.getPolygonData(id, polygonPoints));

    this.leftCanvas.add(polygon);
    this.dummyCanvas.add(polygon);
  }

  getLineData(start3d: Vertices3Df, end3d: Vertices3Df, mode: string, id: string, isStandard: boolean): any {
    const obj = {
      id: id,
      mode: mode,
      isLine: true,
      standard: [],
      isStandard: isStandard,
      isNum: false,
      coordinate: null,
      startEnd: [start3d, end3d],
      unit: this.unitSelected,
      result: {
        // 結果は計測後に代入する
        length: null,
        objectDistance: null,
        area: null,
        latestDistance: null
      }
    };
    return obj;
  }

  // Return the object to be placed
  pointText(isFoot: boolean = false): string {
    switch (this.selectMode) {
      case MEASUREMENT_MODE.DISTANCE:
      case MEASUREMENT_MODE.LINES:
      case MEASUREMENT_MODE.AREA:
        return "×";
      case MEASUREMENT_MODE.POINT_TO_LINE:
        if (isFoot) {
          return "〇";
        }
        return this.isLineReferenceMeasure() || this.plotPointList.length === 2 ? "×" : "◇";
      default:
        return this.isSurfaceReferenceMeasure() || this.plotPointList.length === 3 ? "×" : "◇";
    }
  }

  // Return the size of the object to be placed
  pointSize(isFoot: boolean = false): number {
    if (isFoot) {
      return 12;
    }
    switch (this.selectMode) {
      case MEASUREMENT_MODE.DISTANCE:
      case MEASUREMENT_MODE.LINES:
      case MEASUREMENT_MODE.AREA:
        return 20;
      case MEASUREMENT_MODE.POINT_TO_LINE:
        return this.isLineReferenceMeasure() || this.plotPointList.length === 2 ? 20 : 14;
      default:
        return this.isSurfaceReferenceMeasure() || this.plotPointList.length === 3 ? 20 : 14;
    }
  }

  // Return the color of the object to be placed
  pointColor(isFoot: boolean = false) {
    if (isFoot) {
      return this.measurementColor;
    }
    switch (this.selectMode) {
      case MEASUREMENT_MODE.DISTANCE:
      case MEASUREMENT_MODE.LINES:
      case MEASUREMENT_MODE.AREA:
        return this.measurementColor;
      case MEASUREMENT_MODE.POINT_TO_LINE:
        if (!this.isLineReferenceMeasure() && this.plotPointList.length <= 1) {
          return this.referenceLineColor;
        }
        return this.measurementColor;
      case MEASUREMENT_MODE.DEPTH:
        if (!this.isSurfaceReferenceMeasure() && this.plotPointList.length <= 2) {
          return this.referenceLineColor;
        }
        return this.measurementColor;
      default:
        return this.measurementColor;
    }
  }

  /** Toggle the visibility of objects on the screen for 3D model display */
  display3DModeObject(): boolean {
    return this.measurementService.is3DMeasurement;
  }

  async toggle3DView() {
    this.is3DMode = !this.is3DMode;
    this.displayModeSetting();
    if (!this.is3DMode) {
      await this.loadUvImage();
    }
  }

  // Change to 3D mode
  displayModeSetting() {
    const threeDCanvas = document.getElementById("3d-canvas-area");
    const rightCanvas = document.getElementById("right-canvas-area");
    if (this.is3DMode) {
      threeDCanvas.style.display = "none";
      rightCanvas.style.display = "";
    } else {
      threeDCanvas.style.display = "";
      rightCanvas.style.display = "none";
    }
  }

  create3DObject() {
    // Specify size
    const width = this.halfMainImgW;
    const height = this.mainImg.height * this.scale;

    // Create renderer
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(width, height);

    // Create scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color("#ebffff");
    this.scene.rotation.set(0, 0, Math.PI);

    // Create camera (field of view, aspect ratio of the screen, nearest distance visible to the camera, farthest distance visible to the camera)
    this.camera = new THREE.PerspectiveCamera(this.fieldOfView, width / height, this.nearClippingPane, this.farClippingPane);
    this.camera.position.z = -this.cameraZ;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const component: ImageMeasurementEditComponent = this;

    // By setting this, the object can be moved
    new OrbitControls(this.camera, this.renderer.domElement);

    // Allocate array
    const geometry = new THREE.BufferGeometry();
    const vertex = new Float32Array(this.measurementInfoData.model3DData.point_count * 3);
    const uvCoordinates = new Float32Array(this.measurementInfoData.model3DData.point_count * 2);
    const triangleIds = [];

    let z = 0;
    // Serialize vertex data array
    for (let i = 0; i < this.measurementInfoData.model3DData.point_count; i++) {
      vertex[i * 3] = this.measurementInfoData.model3DData.point3D[i].x;
      vertex[i * 3 + 1] = this.measurementInfoData.model3DData.point3D[i].y;
      vertex[i * 3 + 2] = this.measurementInfoData.model3DData.point3D[i].z;
      z += this.measurementInfoData.model3DData.point3D[i].z;
    }

    // Value to bring the 3D object closer to the origin
    // Bringing the 3D object closer to the origin by the average of the z-coordinates improves operability
    this.positionZ = z / this.measurementInfoData.model3DData.point_count;

    // Adjust camera position
    this.camera.position.z = -this.positionZ - 5;
    this.firstCameraPosition = JSON.parse(JSON.stringify(this.camera.position));

    // Serialize texture (UV coordinates) array
    for (let i = 0; i < this.measurementInfoData.model3DData.point_count; i++) {
      uvCoordinates[i * 2] = this.measurementInfoData.model3DData.textureCoord[i].x;
      uvCoordinates[i * 2 + 1] = this.measurementInfoData.model3DData.textureCoord[i].y;
    }

    // Set polygon ID
    for (let i = 0; i < this.measurementInfoData.model3DData.mesh_count; i++) {
      triangleIds.push(
        this.measurementInfoData.model3DData.triangles[i].iD1,
        this.measurementInfoData.model3DData.triangles[i].iD2,
        this.measurementInfoData.model3DData.triangles[i].iD3
      );
    }

    // Apply settings to geometry
    geometry.setAttribute("position", new THREE.BufferAttribute(vertex, 3));
    geometry.setAttribute("uv", new THREE.BufferAttribute(uvCoordinates, 2));
    geometry.setIndex(triangleIds);

    // Attach UV image as ImageData
    new THREE.TextureLoader().load(this.uvImgSrcBase64, (texture) => {
      // three r152+ renders in sRGB: declare the photo as sRGB so it keeps its colours.
      texture.colorSpace = THREE.SRGBColorSpace;
      const material = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide });

      // Mesh (3D object) = Geometry (shape) + Material (texture)
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(0, 0, -this.positionZ);
      mesh.name = "3dModel";
      this.scene.add(mesh);
    });

    // Display on 3DCanvas if there are saved measurements
    const objList = [];
    this.dummyCanvas._objects.forEach((item) => {
      const data = item.get("data");
      if (
        (item.type === "text" && data.coordinate && !objList.some((obj) => obj.data.coordinate && obj.data.coordinate.x === data.coordinate.x)) ||
        (item.type === "line" &&
          data.startEnd &&
          !objList.some((obj) => obj.data.startEnd && obj.data.startEnd[0].x === data.startEnd[0].x && obj.data.startEnd[1].x === data.startEnd[1].x))
      ) {
        objList.push(item);
        this.set3dObject(item);
      }
    });

    this.isReady3d = true;
    this.oscSpinnerService.hide(true);
    this.resizeAfterLoading();
    tick();

    // 毎フレーム時に実行されるループイベント
    function tick() {
      requestAnimationFrame(tick);
      component.renderer.render(component.scene, component.camera);
    }
  }

  public resetCameraPosition() {
    this.camera.position.set(this.firstCameraPosition.x, this.firstCameraPosition.y, this.firstCameraPosition.z);
    this.camera.lookAt(new THREE.Vector3(0, 0, 0));
  }
  drawMissingCornerResult3D(points: Vertices3Df[]) {
    // First 4 points are the input points.
    const filteredPoints = points.slice(4, 6);
    const cornerPoints = points.slice(6);
    if (this.isReady3d) {
      cornerPoints.forEach((point) => {
        const geometry = new THREE.BufferGeometry().setFromPoints(filteredPoints.concat(point) as any);
        const material = new THREE.MeshBasicMaterial({ color: this.areaFillColor, opacity: 0.4, transparent: true });
        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.set(0, 0, -this.positionZ);
        this.scene.add(mesh);
      });
    }
  }

  // Display measurement points on the 3D model
  dataset3d(threeDPoint: Vertices3Df, mode: string, id: string, isStandard: boolean, color: string) {
    const SIZE = 0.1;
    const geometry = new THREE.SphereGeometry(SIZE);
    const material = new THREE.MeshBasicMaterial({ color: color });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData = this.getPointData(false, threeDPoint, mode, id, isStandard);
    mesh.position.set(threeDPoint.x, threeDPoint.y, threeDPoint.z - this.positionZ);
    this.scene.add(mesh);
  }

  // Display reference lines and measurement lines on the 3D model
  setLine3d(start: Vertices3Df, end: Vertices3Df, mode: string, id: string, isStandard: boolean, color: string) {
    const material = new THREE.LineBasicMaterial({ color: color });
    const points = [new THREE.Vector3(start.x, start.y, start.z), new THREE.Vector3(end.x, end.y, end.z)];
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const mesh = new THREE.Line(geometry, material);
    mesh.userData = this.getLineData(start, end, mode, id, isStandard);
    mesh.position.set(0, 0, -this.positionZ);
    this.scene.add(mesh);
  }

  // Determine intersection between line segment ab and line segment cd
  isCrossingLines(cx: number, cy: number, dx: number, dy: number) {
    if (this.selectMode !== MEASUREMENT_MODE.AREA) {
      return false;
    }

    const ax = this.plotPointList[0].msr_pnt.x;
    const ay = this.plotPointList[0].msr_pnt.y;
    const bx = this.plotPointList[1].msr_pnt.x;
    const by = this.plotPointList[1].msr_pnt.y;

    const ta = (cx - dx) * (ay - cy) + (cy - dy) * (cx - ax);
    const tb = (cx - dx) * (by - cy) + (cy - dy) * (cx - bx);
    const tc = (ax - bx) * (cy - ay) + (ay - by) * (ax - cx);
    const td = (ax - bx) * (dy - ay) + (ay - by) * (ax - dx);

    return tc * td <= 0 && ta * tb <= 0;
  }

  // Process when the ZoomIn/ZoomOut button is pressed
  changeImageMagnification(zoom: number) {
    this.imageMagnification = zoom;
    this.changeMagnification(this.imageMagnification);
  }

  isDisabled(): boolean {
    let isDisabled = false;
    let noDataId: string;
    this.dummyCanvas._objects.forEach((obj) => {
      const data = obj.get("data");
      if (obj.isType("image")) {
        if (data && !data.result) {
          isDisabled = true;
          noDataId = data.id;
        }
      }
    });

    // If there are measurements of a (already measured) and b (in progress), control so that the measurement of a cannot be moved
    if (noDataId) {
      this.duringMeasureId = noDataId;
      this.leftCanvas._objects.forEach((obj: any) => {
        if (obj.type === "text" && (obj.text === "×" || obj.text === "◇") && obj.data.id === noDataId) {
          obj.set({ selectable: true, hasControls: false });
        } else {
          obj.set({ selectable: false });
        }
      });
    } else {
      this.duringMeasureId = null;
      this.leftCanvas._objects.forEach((obj: any) => {
        if (obj.type === "text" && (obj.text === "×" || obj.text === "◇")) {
          obj.set({ selectable: true, hasControls: false });
        } else {
          obj.set({ selectable: false });
        }
      });
    }
    return isDisabled;
  }

  getScale(): number {
    const mainContent = document.getElementsByClassName("modal__content")[0];
    const headers = document.getElementsByClassName("dialog-nav")[0];
    const rightArea = document.getElementById("right-area");
    const sliderArea = document.getElementById("slider-area");
    const margins = 8;
    const width = (mainContent.clientWidth - rightArea.clientWidth - sliderArea.clientWidth - 96) / this.mainImg.width;
    const height = (mainContent.clientHeight - headers.clientHeight - margins * 2) / this.mainImg.height;
    const scale = Math.min(width, height);
    this.halfMainImgW = (this.mainImg.width * scale) / 2;
    return scale;
  }

  public addResizeEvent() {
    window.addEventListener("resize", () => {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }

      this.resizeTimer = setTimeout(() => {
        if (this.isLoading) {
          this.isWaitLoading = true;
        } else {
          this.resizeWindow();
        }
      }, 1000);
    });
  }

  private redrawMeasurementObjects(canvasType: string, currentJsonData: string): void {
    const jsonData = JSON.parse(currentJsonData);
    const scale = this.scale;

    const measurementObjects = jsonData.objects.filter((obj) => obj.type.toLowerCase() !== "image");
    let xOffset = 0;

    if (canvasType === this.LEFTCANVAS) {
      measurementObjects.forEach((obj) => {
        this.addScaledObject(obj, this.leftCanvas, scale, 0);
      });
    } else if (canvasType === this.RIGHTCANVAS) {
      xOffset = -this.halfMainImgW;
      measurementObjects.forEach((obj) => {
        if (obj.left * scale > (this.mainImg.width * scale) / 2) {
          this.addScaledObject(obj, this.rightCanvas, scale, xOffset);
        }
      });
    } else {
      measurementObjects.forEach((obj) => {
        this.addScaledObject(obj, this.dummyCanvas, 1, 0);
      });
    }

    this.isCanvasLoading = false;
  }

  private addScaledObject(obj: any, canvas: Canvas, scale: number, xOffset: number): void {
    // xOffset must be applied to x1/x2 and polygon points too, not just `left`: fabric v6's
    // Line constructor recomputes left/top from x1/x2 in _setWidthHeight, dropping any shift
    // that lived only on `left`.
    const scaledObj = {
      ...obj,
      left: obj.left * scale + xOffset,
      top: obj.top * scale,
      fontSize: obj.fontSize ? obj.fontSize * scale : undefined,
      strokeWidth: obj.strokeWidth ? obj.strokeWidth * scale : undefined,
      x1: obj.x1 != null ? obj.x1 * scale + xOffset : undefined,
      x2: obj.x2 != null ? obj.x2 * scale + xOffset : undefined,
      y1: obj.y1 != null ? obj.y1 * scale : undefined,
      y2: obj.y2 != null ? obj.y2 * scale : undefined,
      points: obj.points ? obj.points.map((p) => ({ x: p.x * scale + xOffset, y: p.y * scale })) : undefined
    };

    // Create the appropriate Fabric object based on type
    let fabricObj: FabricObject;

    if (obj.type.toLowerCase() === "text") {
      fabricObj = new FabricText(obj.text, scaledObj);
      fabricObj.set({ selectable: obj.text === "×" || obj.text === "◇", hasControls: false });
    } else if (obj.type.toLowerCase() === "line") {
      fabricObj = new Line([scaledObj.x1, scaledObj.y1, scaledObj.x2, scaledObj.y2], scaledObj);
      fabricObj.set({ selectable: false });
    } else if (obj.type.toLowerCase() === "polygon") {
      fabricObj = new Polygon(scaledObj.points, scaledObj);
      fabricObj.set({ selectable: false });
    }

    if (fabricObj) {
      canvas.add(fabricObj);
    }
  }

  public async resizeWindow() {
    this.isCanvasLoading = true;
    // Remove the canvas
    // If you don't remove the canvas once and then add it again, it won't render correctly
    const currentJsonData = this.getPreciseJson();
    await this.leftCanvas.dispose();
    await this.rightCanvas.dispose();
    await this.dummyCanvas.dispose();

    this.scaleSetting(this.imageMagnification);

    this.setupLeftCanvas(this.mainImg);
    this.setupRightCanvas(this.mainImg);
    this.setupDummyCanvas(this.mainImg);
    this.redrawMeasurementObjects(this.LEFTCANVAS, currentJsonData);
    this.redrawMeasurementObjects(this.RIGHTCANVAS, currentJsonData);
    this.redrawMeasurementObjects(this.DUMMYCANVAS, currentJsonData);
    this.collectRenderAll();
    this.setCanvasEvent();
  }

  public resizeAfterLoading() {
    this.isLoading = false;
    if (this.isWaitLoading) {
      this.resizeWindow();
      this.isWaitLoading = false;
    }
  }

  public getScaleBarWidth(): string {
    const leftCanvasArea = document.getElementById("left-canvas-area");
    const rightCanvasArea = document.getElementById("right-canvas-area");
    const threeDCanvasArea = document.getElementById("3d-canvas-area");

    return this.is3DMode && threeDCanvasArea
      ? (leftCanvasArea.clientWidth + threeDCanvasArea.clientWidth).toString() + "px"
      : (leftCanvasArea.clientWidth + rightCanvasArea.clientWidth).toString() + "px";
  }

  public getDspModeTop(): string {
    const leftCanvasArea = document.getElementById("left-canvas-area");

    return (leftCanvasArea.clientHeight - 75).toString() + "px";
  }

  public getImageSizeScale(): number {
    // The scale factor used to display the image in a 600x600px area
    const width = 600 / this.mainImg.width;
    const height = 600 / this.mainImg.height;
    return Math.min(width, height);
  }

  // The saved scale for annotations changed from v2.0.19 to v2.0.20, so it is necessary to apply the scale corresponding to the saved scale
  // A isNewSpecification flag is embedded in the data of image objects saved from v2.0.20 onward
  public getScaleCorrectionJsonData(json: any) {
    const isNewSpecification = json.objects[0].data && json.objects[0].data.isNewSpecification ? true : false;
    const jsonData = JSON.parse(JSON.stringify(json));
    if (isNewSpecification) {
      return jsonData;
    } else {
      const scale = 1 / this.getImageSizeScale();
      jsonData.objects.forEach((obj) => {
        if (obj.type.toLowerCase() === "image") {
          obj.scaleX = obj.scaleY = 1;
        } else {
          obj.left = obj.left * scale;
          obj.top = obj.top * scale;
          obj.fontSize = obj.fontSize * scale;
          obj.strokeWidth = obj.strokeWidth * scale;
          obj.x1 = obj.x1 * scale;
          obj.x2 = obj.x2 * scale;
          obj.y1 = obj.y1 * scale;
          obj.y2 = obj.y2 * scale;
        }
      });
      return jsonData;
    }
  }

  // Determine whether the annotation is movable
  public setSelectMode(event, isMouseOn: boolean) {
    if (isMouseOn) {
      if (
        event &&
        event.target &&
        event.target.text &&
        (event.target.text === "×" || event.target.text === "◇") &&
        (!this.duringMeasureId || this.duringMeasureId === event.target.data.id)
      ) {
        this.isSelectMode =
          this.selectData.length === 0 || (this.selectData.length > 0 && this.selectData.some((_) => _.id === event.target.data.id));
      } else {
        this.isSelectMode = false;
      }
    } else {
      this.isSelectMode = false;
    }
  }

  private async moveObject(): Promise<void> {
    this.oscSpinnerService.show(true);
    this.oscSpinnerService.displayMessage(this.translate.instant("imageRemeasurementEdit.loadingMessageMeasuringProcess"));
    this.isApiError = false;
    this.movePointList = [];
    this.movePointResult = [];
    this.selectData = [];
    const selectObj = this.leftCanvas.getActiveObject();
    this.isDuringMeasure = selectObj && selectObj.get("data") && !selectObj.get("data").result.objectDistance;
    const measureTypes = [this.distance, this.pointToLine, this.depth, this.lines, this.area];
    const mode = measureTypes
      .find((measureType) => measureType.some((_) => _ === selectObj.get("data").mode))
      ?.find((_) => _ === selectObj.get("data").mode);

    // Re-measurement of reference lines/reference surfaces is done for each measurement mode, not for each measurement.
    if (this.pointToLine.some((_) => _ === selectObj.get("data").mode) || this.depth.some((_) => _ === selectObj.get("data").mode)) {
      for (let i = 0; i < this.dummyCanvas._objects.length; i++) {
        const obj = this.dummyCanvas._objects.find((_) => _.type !== "image" && _.get("data").id === i + " : " + mode);
        if (obj) this.selectData.push(obj.get("data"));
      }
    } else {
      const separationId = selectObj
        .get("data")
        .id.split(":")
        .map((_: string) => _.trim());
      const obj = this.dummyCanvas._objects.find((_) => _.type !== "image" && _.get("data").id === separationId[0] + " : " + mode);
      this.selectData.push(obj.get("data"));
    }

    this.selectData.forEach((selectData) => {
      const res = [];
      this.leftCanvas._objects.forEach((_: any) => {
        // Get the measurement point associated with the moved annotation
        if (
          _.type !== "image" &&
          _.left <= this.mainImg.width / 2 &&
          (_.text === "×" || _.text === "◇" || _.text === "〇") &&
          selectData &&
          _.data.id === selectData.id
        ) {
          const obj = JSON.parse(JSON.stringify(_));
          obj.left = obj.left / this.scale;
          obj.top = obj.top / this.scale;
          res.push(obj);
        }
      });
      this.movePointList.push(res);
    });

    await this.remeasureAtMovedPoint();
  }

  private async remeasureAtMovedPoint(): Promise<void> {
    const measureInfo = new MeasurementInfo();
    measureInfo.measurementCount = this.selectData.length;
    measureInfo.measurementDataList = [];
    for (let i = 0; i < this.selectData.length; i++) {
      // Remove unnecessary annotations from the canvas
      this.dummyCanvas._objects = this.dummyCanvas._objects.filter((_) => _.type === "image" || _.get("data").id !== this.selectData[i].id);
      this.leftCanvas._objects = this.leftCanvas._objects.filter((_) => _.type === "image" || _.get("data").id !== this.selectData[i].id);
      this.rightCanvas._objects = this.rightCanvas._objects.filter((_) => _.type === "image" || _.get("data").id !== this.selectData[i].id);
      if (this.measurementService.is3DMeasurement && this.isReady3d) {
        this.scene.children = this.scene.children.filter((item) => item.name === "3dModel" || item.userData.id !== this.selectData[i].id);
      }
      // Remove the specified result from the measurement results
      this.measurementResult = this.measurementResult.filter((_) => _.id && _.id !== this.selectData[i].id);

      const measureData = new MeasurementData();
      measureData.area = this.selectData[i].result.area;
      measureData.depth = this.selectData[i].result.depth;
      measureData.length = this.selectData[i].result.length;
      measureData.objectDistance = this.selectData[i].result.objectDistance;
      measureData.measurementType = this.getMeasurementTypeNumber(this.selectData[i].mode);
      measureData.points = this.getPoints(i, this.selectData[i].mode);
      measureData.references = this.getReferences(i);
      measureData.subResult = 0;

      if (this.isThreeDApplicable && i >= 1) {
        if (this.pointToLine.some((_) => _ === this.selectData[i].mode)) {
          // Insert reference line coordinates
          measureData.points.unshift(measureInfo.measurementDataList[0].points[0], measureInfo.measurementDataList[0].points[1]);
        } else if (this.depth.some((_) => _ === this.selectData[i].mode)) {
          // Insert reference plane coordinates
          measureData.points.unshift(
            measureInfo.measurementDataList[0].points[0],
            measureInfo.measurementDataList[0].points[1],
            measureInfo.measurementDataList[0].points[2]
          );
        }
      }
      measureInfo.measurementDataList.push(measureData);
    }

    if (
      measureInfo.measurementDataList.every((data) =>
        data.points.every((_) => this.isPlotArea(_.x * this.scale * this.imageMagnification, _.y * this.scale * this.imageMagnification))
      )
    ) {
      // If the moved coordinates are within the measurable area
      await this.loadMeasurementData(measureInfo);
      await this.movePointsMeasure();
    } else {
      this.resetMoveMeasure();
    }

    if (this.isApiError) {
      this.resetMoveMeasure();
    } else {
      this.selectData = [];
      this.movePointList = [];
      this.movePointResult = [];
      this.isDuringMeasure = false;
      this.leftCanvas.discardActiveObject();
      this.leftCanvas._objects.forEach((obj: any) => {
        if (obj.type === "text" && (obj.text === "×" || obj.text === "◇")) {
          obj.set({ selectable: true, hasControls: false });
        } else {
          obj.set({ selectable: false });
        }
      });
      this.leftCanvas.renderAll();
    }
  }

  async movePointsMeasure() {
    const type = this.measurementService.getMeasurementType;
    for (let i = 0; i < this.selectData.length; i++) {
      const mode = this.selectData[i].mode;
      const id = this.selectData[i].id;
      let length = null;
      let objDistance = null;
      let area = null;
      let latestDistance = null;
      let plotList;
      if (this.distance.some((_) => _ === mode)) {
        plotList = this.movePointResult;

        if (plotList.length < 2) {
          return;
        }

        await this.measurementService
          .getTwoPointsMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, plotList)
          .then((item) => {
            length = item.distanceResult.distance;
            objDistance = item.distanceResult.pntset_meas[0].pnt_3d.z;
            this.isApiError = this.isApiError ? true : item.returnCode !== 0;
          })
          .catch((err) => this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError")));
      } else if (this.pointToLine.some((_) => _ === mode)) {
        plotList = [];
        plotList.push(this.movePointResult[0]);
        plotList.push(this.movePointResult[1]);
        plotList.push(this.movePointResult[i + 2]);

        if (plotList.length < 3 || !plotList.every((_) => _)) {
          return;
        }

        await this.measurementService
          .getLineReferenceMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, plotList)
          .then((item) => {
            length = item.pointToLineResult.distance;
            objDistance = item.pointToLineResult.pntset_meas.pnt_3d.z;
            this.isApiError = this.isApiError ? true : item.returnCode !== 0;
          })
          .catch((err) => this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError")));
      } else if (this.depth.some((_) => _ === mode)) {
        plotList = [];
        plotList.push(this.movePointResult[0]);
        plotList.push(this.movePointResult[1]);
        plotList.push(this.movePointResult[2]);
        plotList.push(this.movePointResult[i + 3]);

        if (plotList.some((_) => _ === undefined)) {
          return;
        }

        await this.measurementService
          .getSurfaceReferenceMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, plotList, this.measurementInfoData.model3DData)
          .then((item) => {
            // 基準線を保持しておく
            if (!this.isSurfaceReferenceMeasure()) {
              this.referenceSurface = this.plotPointList;
            }
            length = item.depthResult.distance;
            objDistance = item.depthResult.pntset_meas.pnt_3d.z;
            this.isApiError = this.isApiError ? true : item.returnCode !== 0;
          })
          .catch((err) => this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError")));
      } else if (this.lines.some((_) => _ === mode)) {
        plotList = type === MEASUREMENT_IMAGE_MODE.IV7 ? new AreaLinesIV7() : new AreaLines3D();
        const pointLists = [];
        this.movePointResult.forEach((item) => pointLists.push(item));
        plotList.pntCount = this.movePointResult.length;
        plotList.pointSets = pointLists;

        if (pointLists.length <= 1) {
          return;
        }

        await Promise.all([
          this.measurementService.getOverallLengthMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, plotList),
          this.measurementService.getTwoPointsMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, [
            this.movePointResult[this.movePointResult.length - 1],
            this.movePointResult[this.movePointResult.length - 2]
          ])
        ])
          .then((item) => {
            let linesResult;
            if (this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.IV7) {
              linesResult = (item[0] as LinesMeasureData_IV7_WithReturnCode).linesResult;
            } else if (MEASUREMENT_IMAGE_MODE.NX) {
              linesResult = (item[0] as LinesMeasureData_NX_WithReturnCode).linesResult;
            } else {
              linesResult = (item[0] as AreaLinesMeasureData_WithReturnCode).areaLinesResult;
            }
            length = linesResult.length;
            objDistance = linesResult.pntset_meas[this.movePointResult.length - 1].pnt_3d.z;
            latestDistance = item[1].distanceResult.distance;
            this.isApiError = this.isApiError ? true : item[0].returnCode !== 0 || item[1].returnCode !== 0;
            this.retentionResult = [];
            this.plotPointList = [];
          })
          .catch((err) => this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError")));
      } else if (this.area.some((_) => _ === mode)) {
        plotList = type === MEASUREMENT_IMAGE_MODE.IV7 ? new AreaLinesIV7() : new AreaLines3D();
        const pointLists = [];
        this.movePointResult.forEach((item) => pointLists.push(item));
        plotList.pntCount = this.movePointResult.length;
        plotList.pointSets = pointLists;

        if (pointLists.length < 4 || (this.isDuringMeasure && JSON.stringify(pointLists[0]) !== JSON.stringify(pointLists[pointLists.length - 1]))) {
          return;
        }

        await Promise.all([
          this.measurementService.getAreaMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, plotList),
          this.measurementService.getOverallLengthMeasure(this.inspectionImage.inspectionId, this.inspectionImage.id, plotList)
        ])
          .then((item) => {
            let linesResult;
            if (this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.IV7) {
              linesResult = (item[1] as LinesMeasureData_IV7_WithReturnCode).linesResult;
            } else if (MEASUREMENT_IMAGE_MODE.NX) {
              linesResult = (item[1] as LinesMeasureData_NX_WithReturnCode).linesResult;
            } else {
              linesResult = (item[1] as AreaLinesMeasureData_WithReturnCode).areaLinesResult;
            }
            area = item[0].areaResult.area;
            objDistance = item[0].areaResult.pntset_meas[0].pnt_3d.z;
            length = linesResult.length;
            this.isApiError = this.isApiError ? true : item[0].returnCode !== 0 || item[1].returnCode !== 0;
            this.retentionResult = [];
            this.plotPointList = [];
          })
          .catch((err) => this.rollBackMeasurement(err, this.translate.instant("imageRemeasurementEdit.measureError")));
      }

      if (!this.isApiError) {
        this.updateObjectInfoOnCanvas(length, objDistance, id, area, latestDistance);
        if (
          (this.lines.some((_) => _ === mode) && !this.measurementResult.some((_) => _.id === id)) ||
          (this.area.some((_) => _ === mode) && this.isDuringMeasure)
        ) {
          this.measurementResult.push(
            MeasurementResultFactory.createMeasurementResult(new PlotResultGroup(id, mode, length, objDistance, area, latestDistance, plotList))
          );
        }

        this.measurementResult.sort((a, b) => Number(a.id.slice(0, 1)) - Number(b.id.slice(0, 1)));
        this.selectResult = this.measurementResult.find((_) => _.id === id);
      }
    }
  }

  public getMeasurementTypeNumber(mode: string): number {
    const measureTypeMappingsIV7 = [this.distance, this.pointToLine, this.lines, this.depth, this.area];
    const measureTypeMappingsOther = [this.distance, this.pointToLine, this.depth, this.area, this.lines];
    const imgMeasureType = this.measurementService.getMeasurementType;
    const priorityMappings = imgMeasureType === MEASUREMENT_IMAGE_MODE.IV7 ? measureTypeMappingsIV7 : measureTypeMappingsOther;

    for (let i = 0; i < priorityMappings.length; i++) {
      if (priorityMappings[i].some((_) => _ === mode)) {
        return i + 1;
      }
    }
    return 0;
  }

  public getPoints(i: number, mode: string): any {
    let points;
    if (this.measurementService.getMeasurementType === MEASUREMENT_IMAGE_MODE.IV7) {
      points = this.movePointList[i]
        .filter((_) => _.text === "×" || _.text === "〇")
        .map((_) => {
          return { x: _.left, y: _.top };
        });
    } else {
      points = this.movePointList[i]
        .filter((_) => _.text === "×" || _.text === "◇")
        .map((_) => {
          return { x: _.left, y: _.top };
        });
    }

    if (this.area.some((_) => _ === mode) && !this.isDuringMeasure) {
      // Since the first point and the last point are the same coordinates, insert the first point
      points.push(points[0]);
    }
    return points;
  }

  public getReferences(i: number): any {
    const references = this.movePointList[i]
      .filter((_) => _.text === "◇")
      .map((obj) => {
        return { x: obj.left, y: obj.top };
      });

    return i === 1 || this.isThreeDApplicable ? null : references;
  }

  // Temporarily save the annotation information and measurement results before moving the annotation
  public keepAnnotationAndResult() {
    this.tmpLCanvas = JSON.parse(JSON.stringify(this.leftCanvas.toDatalessJSON(["data"])));
    this.tmpRCanvas = JSON.parse(JSON.stringify(this.rightCanvas.toDatalessJSON(["data"])));
    this.tmpDCanvas = JSON.parse(JSON.stringify(this.dummyCanvas.toDatalessJSON(["data"])));
    this.tmpTCanvas = this.measurementService.is3DMeasurement && this.isReady3d ? this.scene.children : null;
    this.tmpMeasureResult = this.measurementResult;
  }

  // Revert to the state before the annotation was moved
  public resetMoveMeasure() {
    this.oscSpinnerService.show(true);
    let isDummy = false;
    let isLeft = false;
    let isRight = false;
    this.leftCanvas.loadFromJSON(this.tmpLCanvas, () => {
      isLeft = true;
      if (isDummy && isLeft && isRight) {
        this.oscSpinnerService.hide(true);
        this.setCanvasEvent();
      }
      this.leftCanvas.renderAll();
    });
    this.rightCanvas.loadFromJSON(this.tmpRCanvas, () => {
      this.rightCanvas._objects.forEach((obj: any) => obj.set({ selectable: false }));
      isRight = true;
      if (isDummy && isLeft) {
        this.oscSpinnerService.hide(true);
        this.setCanvasEvent();
      }
      this.rightCanvas.renderAll();
    });
    this.dummyCanvas.loadFromJSON(this.tmpDCanvas, () => {
      isDummy = true;
      if (isLeft && isRight) {
        this.oscSpinnerService.hide(true);
        this.setCanvasEvent();
      }
      this.dummyCanvas.renderAll();
    });
    this.measurementResult = this.tmpMeasureResult;
    if (this.measurementService.is3DMeasurement && this.isReady3d) {
      this.scene.children = this.tmpTCanvas;
    }

    this.snackBar.open(this.translate.instant("imageRemeasurementEdit.noMatch"));
  }

  resetObject() {
    const appName = this.isOnPremiseMode ? this.iplexAppName : this.visolAppName;
    const message = this.translate.instant("imageRemeasurementEdit.resetMeasureImageFirstMessage", { fileName: this.inspectionImage.fileName });
    const secondMessage = this.translate.instant("imageRemeasurementEdit.resetMeasureImageSecondMessage", { app: appName });

    const dialogRef = this.dialog.open(CommonConfirmDialogComponent, {
      width: "570px",
      height: "270px",
      data: {
        firstMessage: message,
        secondMessage: secondMessage,
        dialogType: "confirmation",
        trueBtnText: "OK"
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        this.oscSpinnerService.show(true);
        this.measurementNumData = [];
        this.referenceSurface = [];
        this.referenceLine = [];
        this.selectData = [];
        this.movePointList = [];
        this.retentionResult = [];
        this.plotPointList = [];
        this.movePointResult = [];
        this.isDuringMeasure = false;
        this.isWaitLoading = true;
        this.isImageInfo = false;

        await this.leftCanvas.dispose();
        await this.rightCanvas.dispose();
        await this.dummyCanvas.dispose();

        this.measurementService.setInspectionImage(this.inspectionImage);
        await this.getAndGenerationOfMeasureInfo(true);
        await this.resizeWindow();
      }
    });
  }

  getRaisedButtonArea() {
    const rightArea = document.getElementById("right-area");
    const modeArea = document.getElementById("mode-area");
    const resultArea = document.getElementById("result-area");
    const height = rightArea.clientHeight - (modeArea.clientHeight + resultArea.clientHeight);

    return height.toString() + "px";
  }

  initCanvasAndResult() {
    this.dummyCanvas._objects = this.dummyCanvas._objects.filter((obj) => obj.isType("image"));
    this.rightCanvas._objects = this.rightCanvas._objects.filter((obj) => obj.isType("image"));
    this.leftCanvas._objects = this.leftCanvas._objects.filter((obj) => obj.isType("image"));
    if (this.measurementService.is3DMeasurement && this.isReady3d) {
      this.scene.children = this.scene.children.filter((obj) => obj.name === "3dModel");
    }

    this.measurementResult = [];
    this.collectRenderAll();
  }

  openSelectModeDialog() {
    const dialogRef = this.dialog.open(SelectMeasurementModeDialogComponent, {
      height: "516px",
      data: {
        selectMode: this.selectMode
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.changeMode(result);
      }
    });
  }

  isSelectModeImg(mode: string): boolean {
    return this.selectMode === mode;
  }

  getModeImageLeft(): string {
    const editContent = document.getElementsByClassName("edit-content");
    const leftCanvas = document.getElementsByClassName("left-canvas-area");
    const rightCanvas = document.getElementsByClassName("right-canvas-area");
    const rightArea = document.getElementsByClassName("right-area");

    if (editContent && leftCanvas && rightCanvas && rightArea) {
      const editWidth = editContent[0].clientWidth;
      const leftCanvasWidth = leftCanvas[0].clientWidth;
      const rightCanvasWidth = rightCanvas[0].clientWidth;
      const rightWidth = rightArea[0].clientWidth;
      return this.selectMode === MEASUREMENT_MODE.DISTANCE || this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE
        ? ((editWidth - (leftCanvasWidth + rightCanvasWidth + rightWidth)) / 2).toString() + "px"
        : ((editWidth - (leftCanvasWidth + rightCanvasWidth + rightWidth)) / 2 + 10).toString() + "px";
    } else {
      return "0px";
    }
  }

  getModeImageTop(): string {
    return this.selectMode === MEASUREMENT_MODE.DISTANCE || this.selectMode === MEASUREMENT_MODE.POINT_TO_LINE ? "117px" : "127px";
  }

  getModeImageHeight(): string {
    const leftCanvasArea = document.getElementById("left-canvas-area");

    return leftCanvasArea && leftCanvasArea.clientHeight ? (leftCanvasArea.clientHeight / 5).toString() + "px" : "100px";
  }
}

/**
 * Interface for the result of line coordinates.
 * Used as the return value for getLineCoordinatesAsync() and distortionReproductionCurveAcquisitionAsync().
 */
export interface LineCoordinateResult {
  /** Array of PolylineData_IV7_WithReturnCode */
  PolylineDataItems: Array<PolylineData_IV7_WithReturnCode>;
  /** Whether it is necessary to add to the list of measurement results. If addition is necessary, execute pushMeasurementResult() at the caller. */
  requirePushMeasurementResult: boolean;
}
